import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  getDoc,
  setDoc,
  addDoc,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  writeBatch,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
  deleteField,
} from 'firebase/firestore';
import {
  Page,
  DEFAULT_PAGE_SIZE,
  normalisePageSize,
  overfetchLimit,
  splitOverfetch,
  remainingExportBudget,
  exportWasTruncated,
  MAX_EXPORT_ROWS,
} from './pagination';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { normalizeImageUrl, validateImageUrl } from '../utils/imageUrl';
import { db, auth, storage, functions } from '../config/firebase';
import { 
  ServiceSegment, 
  ServiceCategory, 
  ServiceRate,
  CreateServiceSegmentRequest,
  UpdateServiceSegmentRequest,
  CreateServiceCategoryRequest,
  UpdateServiceCategoryRequest,
  CreateServiceRateRequest,
  UpdateServiceRateRequest
} from '../types/services';

/**
 * Task statuses the admin panel treats as "active" (issue #358).
 *
 * Kept as one constant so the tab filter, the count query and the export
 * filter cannot drift apart — previously each view re-typed the list inline.
 */
export const ACTIVE_TASK_STATUSES = ['pending', 'assigned', 'in_progress'] as const;

/**
 * A status filter for the task list: `'all'` for no constraint, or the exact
 * set of statuses to match. Expressed as a set so it can be pushed into the
 * Firestore query instead of applied to a downloaded collection.
 */
export type TaskStatusFilter = 'all' | readonly string[];

/** Anything with an id and a `data()` — a query snapshot or a single doc read. */
type ReadableDoc = { id: string; data: () => DocumentData | undefined };

// Rejection reason options for different field types
export type TextFieldRejectionReason =
  | 'Mismatch with document'
  | 'Incorrect details entered'
  | 'Incomplete information'
  | 'Typo / spelling mismatch';

export type ImageFieldRejectionReason =
  | 'Image is blurred'
  | 'Document not clearly visible'
  | 'Wrong document uploaded'
  | 'Partial image uploaded';

// Field names that can be rejected
export type RejectableFieldName =
  // Personal Details
  | 'fullName'
  | 'dateOfBirth'
  | 'gender'
  // Address Details
  | 'permanentAddress'
  | 'currentAddress'
  // Document Numbers
  | 'aadharNumber'
  | 'dlNumber'
  | 'panNumber'
  // Document Images
  | 'aadharFrontImage'
  | 'aadharBackImage'
  | 'dlImage'
  | 'panImage';

// Rejected fields with reasons
export interface RejectedFields {
  [fieldName: string]: TextFieldRejectionReason | ImageFieldRejectionReason;
}

// Interface definitions (matching mobile app)
export interface NinjaProfile {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  phone: string;
  phoneNumber: string;
  profileImage?: string;
  dateOfBirth?: string;
  gender?: string;
  panCard: string;
  aadharCard: string;
  dlNumber?: string;
  panCardImage: string;
  aadharCardImage: string;
  aadharBackImage?: string;
  dlImage?: string;
  panCardUrl?: string;
  aadharCardUrl?: string;
  aadharBackUrl?: string;
  dlUrl?: string;
  selectedSegments: string[];
  serviceArea: {
    coordinates: any;
    radius: number;
  };
  bankAccountNumber: string;
  ifscCode: string;
  bankingDetails?: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  permanentAddress?: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  currentAddress?: {
    street: string;
    city: string;
    state: string;
    pincode: string;
  };
  deliveryCapabilities?: {
    fragileItems: boolean;
    maxDistance: number;
    maxWeight: number;
    refrigerated: boolean;
    vehicleType: string;
  };
  verificationStatus: 'pending' | 'approved' | 'rejected' | 'deleted';
  isActive: boolean;
  rating: number;
  totalJobs: number;
  totalEarnings: number;
  wallet?: {
    balance: number;
    totalCredits?: number;
    totalDebits?: number;
    updatedAt?: any;
  };
  // Rejection tracking fields
  rejectedFields?: RejectedFields;
  rejectionReason?: string;
  rejectedAt?: Date;
  // Issue #239: Account deletion fields
  accountStatus?: 'active' | 'deleted';
  deletedAt?: Date;
  deletionReason?: string;
  deletionDetails?: string;
  createdAt: Date;
  updatedAt: Date;
  approvedAt?: Date;
  /** Which city this ninja belongs to. Absent on profiles created before
   *  city-scoping existed — treat missing as "unassigned", not as an error. */
  cityId?: string;
  createdByAdmin?: string;
}

/** A tenant boundary: every city_admin, ninja, and city-scoped rate is scoped
 *  to one of these. See HyperlocalApp/firestore.rules `cities` for the write
 *  gate (super_admin only). */
export interface City {
  id: string;
  name: string;
  state: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type AdminRole = 'super_admin' | 'city_admin';

/** admin_users/{uid} — the one place an admin's role and city live. */
export interface AdminUserRecord {
  uid: string;
  email: string;
  name: string;
  role: AdminRole;
  /** null/absent for super_admin, who isn't scoped to one city. */
  cityId: string | null;
  isActive: boolean;
  createdAt?: Date;
  createdBy?: string;
}

/** A city's on/off + price-override view onto one master service_rates
 *  entry. Document id is `{cityId}_{rateId}` (enforced by the Firestore
 *  rule), so a city can have at most one override per master rate. */
export interface CityServiceRate {
  id: string;
  cityId: string;
  rateId: string;
  isEnabled: boolean;
  overridePrice?: number;
  overrideUrgencyMultipliers?: {
    standard: number;
    express: number;
    urgent: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface AdminActivityLogEntry {
  id: string;
  adminId: string;
  adminName?: string;
  action: string;
  cityId?: string;
  /** Set when a super_admin performed this action while viewing a city that
   *  isn't their own — i.e. impersonation. */
  actingAsCityId?: string;
  isImpersonating?: boolean;
  ip: string;
  userAgent?: string;
  timestamp: Date;
}

export interface DeliveryTask {
  id: string;
  orderId: string; // PN-XXXXXX format
  userId: string;
  title: string;
  description: string;
  taskType?: 'delivery' | 'service'; // Add task type to distinguish
  
  // Delivery-specific fields
  packageType?: string;
  packageWeight?: string;
  weight?: number; // Added missing weight property
  packageValue?: string;
  points?: any[];
  pickupLocations?: Array<{ address: string; contactName: string; contactPhone: string }>; // Added missing property
  dropLocations?: Array<{ address: string; contactName: string; contactPhone: string }>; // Added missing property
  
  // Service-specific fields
  serviceId?: string;
  serviceName?: string;
  category?: string;
  duration?: string;
  preferredDate?: Date;
  preferredTime?: string;
  serviceLocation?: any;
  
  // Common fields
  urgency: 'standard' | 'express' | 'urgent' | 'immediate' | 'scheduled' | 'flexible';
  specialInstructions: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  assignedTo?: string;
  estimatedPrice: number;
  finalPrice?: number;
  createdAt: Date;
  updatedAt: Date;
  scheduledFor?: Date;
  completedAt?: Date;
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  paymentMethod?: 'cash' | 'card' | 'upi' | 'wallet';
  rating?: number;
  review?: string;
  photos?: string[];
  
  // Coupon/Discount details
  appliedCoupon?: {
    id: string;
    code: string;
    title: string;
    type: 'flat' | 'percentage';
    value: number;
    maxDiscount?: number;
  };
  couponDiscount?: number;
  
  // Route and pricing data (calculated and saved)
  routeData?: {
    totalDistance: number; // Total route distance in km
    totalTime: number; // Total estimated time in minutes
    pointDistances: number[]; // Distance between consecutive points
    pointTimes: number[]; // Time between consecutive points
  };
  
  // Invoice/Cart details
  invoiceData?: {
    subtotal: number;
    deliveryCharges: number;
    urgencyCharges: number;
    weightCharges: number;
    insuranceCharges: number;
    taxes: number;
    couponDiscount?: number;
    total: number;
    breakdown: Array<{
      item: string;
      amount: number;
      description?: string;
    }>;
  };
  
  // Cart items for future e-commerce integration
  cartItems?: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
    image?: string;
    vendorId?: string;
  }>;
  
  customerInfo?: { // Added missing customer info
    name: string;
    email: string;
    phone: string;
  };
  ninjaInfo?: { // Added missing ninja info
    name: string;
    phone: string;
  };
}

/** A saved delivery address from a customer's `addresses` subcollection. */
export interface CustomerAddress {
  id: string;
  label: string;
  address: string;
  isDefault: boolean;
  alias?: string; // Issue #262: alias field for home-type priority
  fullAddress?: string; // Issue #262: fullAddress field from addresses subcollection
}

export interface Customer {
  id: string;
  displayName: string;
  name: string; // Added missing name property
  email: string;
  phoneNumber: string;
  phone: string; // Added missing phone property
  createdAt: Date;
  lastLoginAt?: Date;
  totalTasks: number;
  totalTask?: number; // Backend field name (singular)
  totalSpent: number;
  isVerified: boolean;
  status: 'active' | 'suspended' | 'banned' | 'deleted';
  // Issue #239: Account deletion fields
  deletedAt?: Date;
  deletionReason?: string;
  deletionDetails?: string;
  // Issue #215, #262: Customer address fields
  currentLocation?: {
    address: string;
  };
  addresses?: CustomerAddress[];
}

export interface AppSettings {
  id: string;
  generalSettings: {
    companyName: string;
    supportEmail: string;
    supportPhone: string;
    maxDeliveryRadius: number;
    defaultCurrency: string;
    timezone: string;
    gstNumber?: string;
    gstPercentage?: number;
    platformCharge: {
      type: 'flat' | 'percentage';
      value: number;
    };
    payoutCharge: {
      type: 'flat' | 'percentage';
      value: number;
    };
    maxDeliveryDistance: number;
    supportContact: {
      email: string;
      phone: string;
    };
    // Issue #167: Minimum ninja withdrawal amount (default ₹1000)
    minimumNinjaWithdrawalAmount?: number;
    minimumCustomerWithdrawalAmount?: number;
    // Issue #184: Configurable ninja slot time range
    ninjaSlotStartTime?: string; // e.g., "8:00 AM" or "7:00 AM"
    ninjaSlotEndTime?: string;   // e.g., "8:00 PM" or "9:00 PM"
    // Issue #316: Cancellation deduction duration in minutes
    cancellationDeductionDuration?: number; // Minutes before slot start when deduction applies
  };
  ninjaTravelTime?: number; // Travel time in minutes to add to each task
  updatedAt: Date;
  updatedBy: string;
}

export interface AdminNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'task' | 'ninja' | 'system';
  priority: 'low' | 'medium' | 'high' | 'critical';
  isRead: boolean;
  createdAt: Date;
  readAt?: Date;
  actionUrl?: string; // URL to navigate when clicked
  actionText?: string; // Text for action button
  relatedId?: string; // ID of related task, ninja, etc.
  relatedType?: 'task' | 'ninja' | 'customer' | 'payment';
  metadata?: Record<string, any>; // Additional data
}

export interface Banner {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  type: 'advertisement' | 'local_shop' | 'promotion' | 'announcement';
  targetUrl?: string;
  targetSegmentId?: string; // Show banner only for specific segment
  targetCategoryId?: string; // Show banner only for specific category
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  priority: number; // Higher number = higher priority
  clickCount: number;
  impressionCount: number;
  budget?: number; // For paid advertisements
  budgetSpent?: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface BannerAd {
  id: string;
  imageUrl: string;
  bannerText: string;
  linkType: 'external' | 'internal';
  externalLink?: string;
  internalLink?: string; // Service segment ID
  displayOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

class AdminService {
  private readonly NINJAS_COLLECTION = 'ninjas';
  private readonly TASKS_COLLECTION = 'tasks';
  private readonly USERS_COLLECTION = 'users';
  private readonly SETTINGS_COLLECTION = 'app_settings';
  private readonly SEGMENTS_COLLECTION = 'service_segments';
  private readonly CATEGORIES_COLLECTION = 'service_categories';
  private readonly RATES_COLLECTION = 'service_rates';
  private readonly BANNERS_COLLECTION = 'banners';
  private readonly BANNER_ADS_COLLECTION = 'banner_ads';
  private readonly CITIES_COLLECTION = 'cities';
  private readonly ADMIN_USERS_COLLECTION = 'admin_users';
  private readonly CITY_SERVICE_RATES_COLLECTION = 'city_service_rates';

  // Ninja Management
  /**
   * Loads a ninja's identity and banking details (issue #352).
   *
   * These live in `ninjas/{id}/private/kyc` rather than on the profile
   * document, because the profile is readable by every authenticated user and
   * these fields are Aadhaar, PAN, driving licence and bank account details.
   * Only the ninja themselves and admins can read this subcollection.
   *
   * Fetched on demand when an application is opened for review, rather than
   * with the list, so the data is read only when it is actually needed.
   *
   * Falls back to an empty object for profiles that predate the migration —
   * their fields are still on the profile document, so the caller's existing
   * lookups continue to work until `qa/migrations/move-ninja-kyc.js` has run.
   */
  async getNinjaPrivateDetails(ninjaId: string): Promise<Record<string, any>> {
    try {
      const snap = await getDoc(doc(db, this.NINJAS_COLLECTION, ninjaId, 'private', 'kyc'));
      return snap.exists() ? (snap.data() as Record<string, any>) : {};
    } catch (error) {
      console.error(`Failed to load private details for ninja ${ninjaId}:`, error);
      return {};
    }
  }

  /** Shape a ninja document the way every ninja view expects it. */
  private mapNinjaDoc(doc: ReadableDoc): NinjaProfile {
    const data = doc.data() || {};
    // Issue #239: Map accountStatus to verificationStatus for deleted accounts
    const verificationStatus = data.accountStatus === 'deleted'
      ? 'deleted'
      : (data.verificationStatus || 'pending');

    return {
      id: doc.id,
      ...data,
      // Handle phone number field consistency
      phoneNumber: data.phoneNumber || data.phone || '',
      phone: data.phone || data.phoneNumber || '',
      // Handle banking details structure
      bankingDetails: data.bankingDetails || {
        accountNumber: data.bankAccountNumber || '',
        ifscCode: data.ifscCode || '',
        bankName: data.bankName || ''
      },
      // Handle image URLs - prefer URLs over local paths
      panCardUrl: data.panCardUrl || data.panCardImage || '',
      aadharCardUrl: data.aadharCardUrl || data.aadharCardImage || '',
      // Issue #239: Override verificationStatus for deleted accounts
      verificationStatus,
      // Handle dates
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      approvedAt: data.approvedAt?.toDate() || undefined,
      // Issue #239: Account deletion fields
      deletedAt: data.deletedAt?.toDate() || undefined,
      deletionReason: data.deletionReason || undefined,
      deletionDetails: data.deletionDetails || undefined,
    } as NinjaProfile;
  }

  /** The base ninja query for a verification-status filter, newest first. */
  private ninjaQueryForStatus(status: string, cityId?: string | null): Query<DocumentData> {
    const ninjasRef = collection(db, this.NINJAS_COLLECTION);
    const clauses = [] as ReturnType<typeof where>[];
    if (cityId) clauses.push(where('cityId', '==', cityId));
    if (status !== 'all') clauses.push(where('verificationStatus', '==', status));
    return query(ninjasRef, ...clauses, orderBy('createdAt', 'desc'));
  }

  /**
   * One page of ninja profiles, newest first (issue #358).
   *
   * The ninjas collection grows with headcount rather than with transaction
   * volume, so this is the least urgent of the three list views — but an
   * unbounded query is unbounded, and the page is the same shape as the others.
   */
  async getNinjasPage(options: {
    status?: string;
    pageSize?: number;
    cursor?: unknown | null;
    /** A city_admin's own city, or a super_admin impersonating one — pass
     *  useAdmin()'s effectiveCityId straight through. Omitted/null means
     *  "every city", which is only ever a super_admin's own view. */
    cityId?: string | null;
  } = {}): Promise<Page<NinjaProfile>> {
    this.requireAdminSession('getNinjasPage');
    return this.fetchPage(
      this.ninjaQueryForStatus(options.status ?? 'all', options.cityId),
      options.pageSize ?? DEFAULT_PAGE_SIZE,
      options.cursor ?? null,
      (d) => this.mapNinjaDoc(d)
    );
  }

  /** Ninja counts per verification status, computed server-side. */
  async getNinjaStatusCounts(cityId?: string | null): Promise<{
    all: number;
    pending: number;
    approved: number;
    rejected: number;
  }> {
    this.requireAdminSession('getNinjaStatusCounts');
    const ninjasRef = collection(db, this.NINJAS_COLLECTION);
    const cityClause = cityId ? [where('cityId', '==', cityId)] : [];

    const countOf = async (q: Query<DocumentData>): Promise<number> =>
      (await getCountFromServer(q)).data().count;

    const [all, pending, approved, rejected] = await Promise.all([
      countOf(query(ninjasRef, ...cityClause)),
      countOf(query(ninjasRef, ...cityClause, where('verificationStatus', '==', 'pending'))),
      countOf(query(ninjasRef, ...cityClause, where('verificationStatus', '==', 'approved'))),
      countOf(query(ninjasRef, ...cityClause, where('verificationStatus', '==', 'rejected'))),
    ]);

    return { all, pending, approved, rejected };
  }

  /**
   * Every ninja profile, paged and capped.
   *
   * Still a full-collection read, but in bounded pages with a hard stop, and
   * only for the two callers that genuinely rank or export every partner.
   */
  async getAllNinjas(cityId?: string | null): Promise<NinjaProfile[]> {
    try {
      this.requireAdminSession('getAllNinjas');
      const { rows, truncated } = await this.fetchAllPaged(
        this.ninjaQueryForStatus('all', cityId),
        (d) => this.mapNinjaDoc(d)
      );
      if (truncated) {
        console.warn(`⚠️ [getAllNinjas]: stopped at the ${MAX_EXPORT_ROWS}-row read cap.`);
      }
      return rows;
    } catch (error) {
      console.error('Error fetching ninjas:', error);
      return [];
    }
  }

  async getPendingNinjas(): Promise<NinjaProfile[]> {
    try {
      const ninjasRef = collection(db, this.NINJAS_COLLECTION);
      const q = query(
        ninjasRef, 
        where('verificationStatus', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle phone number field consistency
          phoneNumber: data.phoneNumber || data.phone || '',
          phone: data.phone || data.phoneNumber || '',
          // Handle banking details structure
          bankingDetails: data.bankingDetails || {
            accountNumber: data.bankAccountNumber || '',
            ifscCode: data.ifscCode || '',
            bankName: data.bankName || ''
          },
          // Handle image URLs - prefer URLs over local paths
          panCardUrl: data.panCardUrl || data.panCardImage || '',
          aadharCardUrl: data.aadharCardUrl || data.aadharCardImage || '',
          // Handle dates
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          approvedAt: data.approvedAt?.toDate() || undefined,
        };
      }) as NinjaProfile[];
    } catch (error) {
      console.error('Error fetching pending ninjas:', error);
      return [];
    }
  }

  async approveNinja(ninjaId: string): Promise<boolean> {
    try {
      const ninjaRef = doc(db, this.NINJAS_COLLECTION, ninjaId);
      await updateDoc(ninjaRef, {
        verificationStatus: 'approved',
        approvedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Clear rejection data on approval
        rejectedFields: null,
        rejectionReason: null,
        rejectedAt: null,
      });
      await this.logAdminActivity(`Approved ninja ${ninjaId}`);
      return true;
    } catch (error) {
      console.error('Error approving ninja:', error);
      return false;
    }
  }

  async rejectNinja(ninjaId: string, reason?: string, rejectedFields?: RejectedFields): Promise<boolean> {
    try {
      const ninjaRef = doc(db, this.NINJAS_COLLECTION, ninjaId);
      const updateData: any = {
        verificationStatus: 'rejected',
        rejectionReason: reason || '',
        rejectedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Add field-level rejection data if provided
      if (rejectedFields && Object.keys(rejectedFields).length > 0) {
        updateData.rejectedFields = rejectedFields;
      }

      await updateDoc(ninjaRef, updateData);
      await this.logAdminActivity(`Rejected ninja ${ninjaId}${reason ? `: ${reason}` : ''}`);
      return true;
    } catch (error) {
      console.error('Error rejecting ninja:', error);
      return false;
    }
  }

  async toggleNinjaStatus(ninjaId: string, isActive: boolean): Promise<boolean> {
    try {
      const ninjaRef = doc(db, this.NINJAS_COLLECTION, ninjaId);
      await updateDoc(ninjaRef, {
        isActive: isActive,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error toggling ninja status:', error);
      return false;
    }
  }

  // Wallet Management
  async getNinjaWallet(userId: string): Promise<any> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getNinjaWallet]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getNinjaWallet]: Admin authenticated:', currentUser.email);
      console.log(`🔍 [getNinjaWallet]: Looking for wallet with ninja userId: ${userId}`);

      let walletData: any = null;

      // PRIMARY APPROACH: Query ninja_wallets where ninjaId === ninja.userId
      try {
        console.log(`🔍 [getNinjaWallet]: Querying ninja_wallets where ninjaId == ${userId}`);
        const walletsRef = collection(db, 'ninja_wallets');
        const q1 = query(walletsRef, where('ninjaId', '==', userId));
        const querySnapshot = await getDocs(q1);

        if (!querySnapshot.empty) {
          const walletDoc = querySnapshot.docs[0];
          console.log(`✅ [getNinjaWallet]: Found wallet using ninjaId == ${userId} query`);
          walletData = {
            id: walletDoc.id,
            ...walletDoc.data()
          };
        } else {
          console.log(`❌ [getNinjaWallet]: No wallet found for ninjaId == ${userId}`);
        }
      } catch (error) {
        console.error('❌ Error querying ninja_wallets with ninjaId:', error);
      }

      // SECONDARY APPROACH: Try wallet document with userId as document ID
      if (!walletData) {
        try {
          console.log(`🔍 [getNinjaWallet]: Trying direct document access with userId: ${userId}`);
          const walletRef = doc(db, 'ninja_wallets', userId);
          const walletDoc = await getDoc(walletRef);

          if (walletDoc.exists()) {
            console.log(`✅ [getNinjaWallet]: Found wallet using document ID approach with userId: ${userId}`);
            walletData = {
              id: walletDoc.id,
              ...walletDoc.data()
            };
          } else {
            console.log(`❌ [getNinjaWallet]: No wallet document found with ID: ${userId}`);
          }
        } catch (error) {
          console.error('❌ Error accessing wallet document directly:', error);
        }
      }

      // FALLBACK APPROACH: Query by userId field (alternative field name)
      if (!walletData) {
        try {
          console.log(`🔍 [getNinjaWallet]: Querying ninja_wallets where userId == ${userId}`);
          const walletsRef = collection(db, 'ninja_wallets');
          const q2 = query(walletsRef, where('userId', '==', userId));
          const querySnapshot2 = await getDocs(q2);

          if (!querySnapshot2.empty) {
            const walletDoc = querySnapshot2.docs[0];
            console.log(`✅ [getNinjaWallet]: Found wallet using userId == ${userId} field query`);
            walletData = {
              id: walletDoc.id,
              ...walletDoc.data()
            };
          } else {
            console.log(`❌ [getNinjaWallet]: No wallet found for userId == ${userId}`);
          }
        } catch (error) {
          console.error('❌ Error querying ninja_wallets with userId:', error);
        }
      }

      // FINAL FALLBACK: Check for embedded wallet in ninja document
      if (!walletData) {
        try {
          console.log(`🔍 [getNinjaWallet]: Checking for embedded wallet in ninja document`);

          // First try to find ninja by userId field
          const ninjasRef = collection(db, this.NINJAS_COLLECTION);
          const ninjaQuery = query(ninjasRef, where('userId', '==', userId));
          const ninjaSnapshot = await getDocs(ninjaQuery);

          if (!ninjaSnapshot.empty) {
            const ninjaDoc = ninjaSnapshot.docs[0];
            const ninjaData = ninjaDoc.data();

            if (ninjaData.wallet) {
              console.log(`✅ [getNinjaWallet]: Found embedded wallet in ninja document`);
              walletData = {
                id: `embedded_${ninjaDoc.id}`,
                balance: ninjaData.wallet.balance || 0,
                totalCredits: ninjaData.wallet.totalCredits || 0,
                totalDebits: ninjaData.wallet.totalDebits || 0,
                updatedAt: ninjaData.wallet.updatedAt || ninjaData.updatedAt,
                source: 'embedded_ninja_wallet'
              };
            }
          }

          // Also try using ninja document ID directly
          if (!walletData) {
            const ninjaDirectRef = doc(db, this.NINJAS_COLLECTION, userId);
            const ninjaDirectDoc = await getDoc(ninjaDirectRef);

            if (ninjaDirectDoc.exists()) {
              const ninjaData = ninjaDirectDoc.data();
              if (ninjaData.wallet) {
                console.log(`✅ [getNinjaWallet]: Found embedded wallet using direct ninja ID`);
                walletData = {
                  id: `embedded_${ninjaDirectDoc.id}`,
                  balance: ninjaData.wallet.balance || 0,
                  totalCredits: ninjaData.wallet.totalCredits || 0,
                  totalDebits: ninjaData.wallet.totalDebits || 0,
                  updatedAt: ninjaData.wallet.updatedAt || ninjaData.updatedAt,
                  source: 'embedded_ninja_wallet'
                };
              }
            }
          }

          if (!walletData) {
            console.log(`❌ [getNinjaWallet]: No embedded wallet found in ninja document`);
          }
        } catch (error) {
          console.error('❌ Error checking embedded wallet in ninja document:', error);
        }
      }

      // If no wallet data found, return null
      if (!walletData) {
        console.log(`❌ [getNinjaWallet]: No wallet found for ninja userId: ${userId} using any approach`);
        return null;
      }

      // Calculate totalWithdrawn from transactions if not present or if using embedded wallet
      if (!walletData.totalWithdrawn || walletData.source === 'embedded_ninja_wallet') {
        try {
          console.log(`🔍 [getNinjaWallet]: Calculating totalWithdrawn from transactions`);
          const transactions = await this.getNinjaTransactions(userId);
          const totalWithdrawn = transactions
            .filter(t => t.type === 'withdrawal' && t.status === 'completed')
            .reduce((sum, t) => sum + (t.amount || 0), 0);

          walletData.totalWithdrawn = totalWithdrawn;
          console.log(`✅ [getNinjaWallet]: Calculated totalWithdrawn: ₹${totalWithdrawn}`);
        } catch (error) {
          console.error('❌ Error calculating totalWithdrawn from transactions:', error);
          walletData.totalWithdrawn = walletData.totalDebits || 0;
        }
      }

      return walletData;
    } catch (error) {
      console.error('❌ [getNinjaWallet]: Error fetching ninja wallet:', error);
      return null;
    }
  }

  async getNinjaTransactions(userId: string): Promise<any[]> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getNinjaTransactions]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getNinjaTransactions]: Admin authenticated:', currentUser.email);
      console.log(`🔍 [getNinjaTransactions]: Looking for transactions with ninja userId: ${userId}`);
      
      let transactions: any[] = [];
      
      // PRIMARY APPROACH: Get transactions from ninja_wallet_transactions where ninjaId === ninja.userId
      try {
        console.log(`🔍 [getNinjaTransactions]: Querying ninja_wallet_transactions where ninjaId == ${userId}`);
        const ninjaWalletTransactionsRef = collection(db, 'ninja_wallet_transactions');
        const q1 = query(
          ninjaWalletTransactionsRef,
          where('ninjaId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot1 = await getDocs(q1);
        
        const walletTransactions = querySnapshot1.docs.map(doc => ({
          id: doc.id,
          type: 'wallet_transaction',
          source: 'ninja_wallet_transactions',
          ...doc.data()
        }));
        
        transactions = [...transactions, ...walletTransactions];
        
        if (walletTransactions.length > 0) {
          console.log(`✅ Found ${walletTransactions.length} ninja wallet transactions using ninjaId == ${userId}`);
        } else {
          console.log(`❌ No ninja wallet transactions found for ninjaId == ${userId}`);
        }
      } catch (error) {
        console.error('❌ Error querying ninja_wallet_transactions with ninjaId:', error);
      }
      
      // SECONDARY APPROACH: Try ninja_wallet_transactions with userId field (alternative field name)
      try {
        console.log(`🔍 [getNinjaTransactions]: Querying ninja_wallet_transactions where userId == ${userId}`);
        const ninjaWalletTransactionsRef = collection(db, 'ninja_wallet_transactions');
        const q2 = query(
          ninjaWalletTransactionsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot2 = await getDocs(q2);
        
        const userIdTransactions = querySnapshot2.docs.map(doc => ({
          id: doc.id,
          type: 'wallet_transaction',
          source: 'ninja_wallet_transactions_userId',
          ...doc.data()
        }));
        
        transactions = [...transactions, ...userIdTransactions];
        
        if (userIdTransactions.length > 0) {
          console.log(`✅ Found ${userIdTransactions.length} ninja wallet transactions using userId == ${userId}`);
        } else {
          console.log(`❌ No ninja wallet transactions found for userId == ${userId}`);
        }
      } catch (error) {
        console.error('❌ Error querying ninja_wallet_transactions with userId:', error);
      }
      
      // FALLBACK APPROACH: Try general transactions collection with ninjaId field
      try {
        console.log(`🔍 [getNinjaTransactions]: Querying transactions where ninjaId == ${userId}`);
        const transactionsRef = collection(db, 'transactions');
        const q3 = query(
          transactionsRef,
          where('ninjaId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot3 = await getDocs(q3);
        
        const generalTransactions = querySnapshot3.docs.map(doc => ({
          id: doc.id,
          type: 'general_transaction',
          source: 'transactions_ninjaId',
          ...doc.data()
        }));
        
        transactions = [...transactions, ...generalTransactions];
        
        if (generalTransactions.length > 0) {
          console.log(`✅ Found ${generalTransactions.length} general transactions using ninjaId == ${userId}`);
        } else {
          console.log(`❌ No general transactions found for ninjaId == ${userId}`);
        }
      } catch (error) {
        console.error('❌ Error querying transactions with ninjaId:', error);
      }
      
      // ADDITIONAL FALLBACK: Try general transactions collection with userId field
      try {
        console.log(`🔍 [getNinjaTransactions]: Querying transactions where userId == ${userId}`);
        const transactionsRef = collection(db, 'transactions');
        const q4 = query(
          transactionsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot4 = await getDocs(q4);
        
        const userTransactions = querySnapshot4.docs.map(doc => ({
          id: doc.id,
          type: 'general_transaction',
          source: 'transactions_userId',
          ...doc.data()
        }));
        
        transactions = [...transactions, ...userTransactions];
        
        if (userTransactions.length > 0) {
          console.log(`✅ Found ${userTransactions.length} general transactions using userId == ${userId}`);
        } else {
          console.log(`❌ No general transactions found for userId == ${userId}`);
        }
      } catch (error) {
        console.error('❌ Error querying transactions with userId:', error);
      }
      
      // Remove duplicates and sort by date
      const uniqueTransactions = transactions.filter((transaction, index, self) =>
        index === self.findIndex(t => t.id === transaction.id)
      );
      
      const sortedTransactions = uniqueTransactions.sort((a: any, b: any) => {
        try {
          const aDate = new Date(a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt || 0);
          const bDate = new Date(b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt || 0);
          return bDate.getTime() - aDate.getTime();
        } catch (error) {
          console.warn('Error sorting transactions by date:', error);
          return 0;
        }
      });
      
      console.log(`✅ [getNinjaTransactions]: Returning ${sortedTransactions.length} total transactions for ninja userId: ${userId}`);
      return sortedTransactions;
      
    } catch (error) {
      console.error('❌ [getNinjaTransactions]: Error fetching ninja transactions:', error);
      return [];
    }
  }

  async getNinjaTasks(ninjaId: string): Promise<any[]> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getNinjaTasks]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getNinjaTasks]: Admin authenticated:', currentUser.email);

      let tasks: any[] = [];

      // Try multiple approaches to get ninja tasks

      // Approach 1: Check tasks collection with ninjaId field
      const tasksRef = collection(db, this.TASKS_COLLECTION);
      const q1 = query(
        tasksRef,
        where('ninjaId', '==', ninjaId),
        orderBy('createdAt', 'desc')
      );

      try {
        const querySnapshot1 = await getDocs(q1);
        if (!querySnapshot1.empty) {
          tasks = querySnapshot1.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        }
      } catch (indexError) {
        console.log('Index not available for ninjaId + createdAt, trying alternative...');
      }

      // Approach 2: Check tasks collection with assignedTo field
      if (tasks.length === 0) {
        try {
          const q2 = query(tasksRef, where('assignedTo', '==', ninjaId));
          const querySnapshot2 = await getDocs(q2);
          if (!querySnapshot2.empty) {
            tasks = querySnapshot2.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          }
        } catch (error) {
          console.log('Error querying tasks with assignedTo:', error);
        }
      }

      // Approach 3: Check ninja_tasks collection
      if (tasks.length === 0) {
        const ninjaTasksRef = collection(db, 'ninja_tasks');
        const q3 = query(
          ninjaTasksRef,
          where('ninjaId', '==', ninjaId)
        );

        try {
          const querySnapshot3 = await getDocs(q3);
          tasks = querySnapshot3.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.log('Error querying ninja_tasks:', error);
        }
      }

      // Fetch ratings from ninjas/{ninjaId}/ratings subcollection
      const ratingsMap = new Map<string, number>();
      try {
        const ratingsRef = collection(db, 'ninjas', ninjaId, 'ratings');
        const ratingsSnapshot = await getDocs(ratingsRef);
        ratingsSnapshot.forEach(doc => {
          const data = doc.data();
          // The document ID is the taskId
          ratingsMap.set(doc.id, data.rating);
        });
        console.log(`✅ Found ${ratingsMap.size} ratings for ninja ${ninjaId}`);
      } catch (ratingsError) {
        console.warn('Could not fetch ratings from subcollection:', ratingsError);
      }

      // Merge ratings into tasks
      const tasksWithRatings = tasks.map(task => ({
        ...task,
        rating: task.rating || ratingsMap.get(task.id) || null
      }));

      // Sort by date (newest first)
      return tasksWithRatings.sort((a: any, b: any) => {
        try {
          const aDate = new Date(a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt || 0);
          const bDate = new Date(b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt || 0);
          return bDate.getTime() - aDate.getTime();
        } catch {
          return 0;
        }
      });

    } catch (error) {
      console.error('Error fetching ninja tasks:', error);
      return [];
    }
  }

  // Get recent wallet activity from ninja_wallets document
  async getNinjaWalletActivity(userId: string): Promise<any[]> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getNinjaWalletActivity]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getNinjaWalletActivity]: Admin authenticated:', currentUser.email);
      console.log(`🔍 [getNinjaWalletActivity]: Looking for recent activity for ninja userId: ${userId}`);
      
      // Get the ninja wallet document
      const wallet = await this.getNinjaWallet(userId);
      
      if (!wallet) {
        console.log(`❌ [getNinjaWalletActivity]: No wallet found for ninja userId: ${userId}`);
        return [];
      }
      
      // Extract recent activity from wallet document
      let recentActivity: any[] = [];
      
      // Check if wallet has a recentActivity array
      if (wallet.recentActivity && Array.isArray(wallet.recentActivity)) {
        recentActivity = wallet.recentActivity.map((activity: any) => ({
          ...activity,
          source: 'ninja_wallets_recentActivity',
          type: 'wallet_activity'
        }));
        console.log(`✅ [getNinjaWalletActivity]: Found ${recentActivity.length} recent activities in wallet document`);
      }
      
      // Check if wallet has a transactions array
      if (wallet.transactions && Array.isArray(wallet.transactions)) {
        const walletTransactions = wallet.transactions.map((transaction: any) => ({
          ...transaction,
          source: 'ninja_wallets_transactions',
          type: 'wallet_transaction'
        }));
        recentActivity = [...recentActivity, ...walletTransactions];
        console.log(`✅ [getNinjaWalletActivity]: Found ${walletTransactions.length} transactions in wallet document`);
      }
      
      // Sort by date (most recent first)
      const sortedActivity = recentActivity.sort((a: any, b: any) => {
        try {
          const aDate = new Date(a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt || a.timestamp?.seconds ? a.timestamp.seconds * 1000 : a.timestamp || 0);
          const bDate = new Date(b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt || b.timestamp?.seconds ? b.timestamp.seconds * 1000 : b.timestamp || 0);
          return bDate.getTime() - aDate.getTime();
        } catch (error) {
          console.warn('Error sorting wallet activity by date:', error);
          return 0;
        }
      });
      
      console.log(`✅ [getNinjaWalletActivity]: Returning ${sortedActivity.length} recent wallet activities for ninja userId: ${userId}`);
      return sortedActivity;
      
    } catch (error) {
      console.error('❌ [getNinjaWalletActivity]: Error fetching ninja wallet activity:', error);
      return [];
    }
  }

  // ---------------------------------------------------------------------
  // Paging primitives (issue #358)
  // ---------------------------------------------------------------------

  /**
   * Run one page of a query.
   *
   * Over-fetches by one document so `hasMore` is exact rather than inferred
   * from a full page, and returns the last *kept* document as the cursor so
   * the next page resumes at the right place even when the extra row was
   * discarded.
   */
  private async fetchPage<T>(
    baseQuery: Query<DocumentData>,
    pageSize: number,
    cursor: unknown | null,
    mapRow: (doc: QueryDocumentSnapshot<DocumentData>) => T
  ): Promise<Page<T>> {
    const size = normalisePageSize(pageSize);
    const constraints = cursor
      ? [startAfter(cursor as QueryDocumentSnapshot<DocumentData>), limit(overfetchLimit(size))]
      : [limit(overfetchLimit(size))];

    const snapshot = await getDocs(query(baseQuery, ...constraints));
    const { rows: docs, hasMore } = splitOverfetch(snapshot.docs, size);

    return {
      rows: docs.map(mapRow),
      cursor: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore,
    };
  }

  /**
   * Walk a query page by page up to MAX_EXPORT_ROWS.
   *
   * Used only by the CSV exports, which are deliberate operator actions. The
   * cap is reported back rather than applied silently — a truncated export that
   * looks complete is worse than a refused one.
   */
  private async fetchAllPaged<T>(
    baseQuery: Query<DocumentData>,
    mapRow: (doc: QueryDocumentSnapshot<DocumentData>) => T
  ): Promise<{ rows: T[]; truncated: boolean }> {
    const rows: T[] = [];
    let cursor: unknown | null = null;

    for (;;) {
      const budget = remainingExportBudget(rows.length);
      if (budget === 0) {
        return { rows, truncated: true };
      }

      const page: Page<T> = await this.fetchPage(baseQuery, budget, cursor, mapRow);
      rows.push(...page.rows);

      if (!page.hasMore || page.cursor === null) {
        return { rows, truncated: exportWasTruncated(rows.length, page.hasMore) };
      }
      cursor = page.cursor;
    }
  }

  private requireAdminSession(context: string): void {
    if (!auth.currentUser) {
      console.error(`❌ [${context}]: Admin not authenticated`);
      throw new Error('Authentication required');
    }
  }

  // Task Management

  /** Shape a task document the way every task view expects it. */
  private mapTaskDoc(doc: ReadableDoc): DeliveryTask {
    const data = doc.data() || {};

    // Build customerInfo with multiple fallbacks for both delivery and service tasks
    const customerName = data.customerInfo?.name ||
                         data.customerName ||
                         data.serviceLocation?.contactDetails?.contactName ||
                         data.contactDetails?.contactName ||
                         'Unknown Customer';
    // Ensure phone is always a string (Firestore might store as number)
    const rawPhone = data.customerInfo?.phone ||
                     data.customerPhone ||
                     data.serviceLocation?.contactDetails?.phone ||
                     data.contactDetails?.phone ||
                     '';
    const customerPhone = rawPhone ? String(rawPhone) : '';
    const customerEmail = data.customerInfo?.email ||
                          data.customerEmail ||
                          '';

    return {
      id: doc.id,
      ...data,
      // Handle customer info with fallbacks (enhanced for service tasks)
      customerInfo: {
        name: customerName,
        email: customerEmail,
        phone: customerPhone
      },
      // Handle ninja info with fallbacks
      ninjaInfo: data.ninjaInfo || (data.assignedTo ? {
        name: data.ninjaName || 'Unknown Ninja',
        phone: data.ninjaPhone || ''
      } : undefined),
      // Handle pickup and drop locations with fallbacks
      pickupLocations: data.pickupLocations || (data.pickupLocation ? [data.pickupLocation] : []),
      dropLocations: data.dropLocations || (data.dropLocation ? [data.dropLocation] : []),
      // Handle dates
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate() || undefined,
    } as DeliveryTask;
  }

  /**
   * The base task query for a status filter, newest first.
   *
   * `'all'` means no status constraint. The tab filters are expressed as a
   * status set so they can be pushed into the query with `in` rather than
   * applied to a downloaded collection.
   */
  private taskQueryForStatus(statuses: TaskStatusFilter): Query<DocumentData> {
    const tasksRef = collection(db, this.TASKS_COLLECTION);
    if (statuses === 'all') {
      return query(tasksRef, orderBy('createdAt', 'desc'));
    }
    if (statuses.length === 1) {
      return query(tasksRef, where('status', '==', statuses[0]), orderBy('createdAt', 'desc'));
    }
    return query(tasksRef, where('status', 'in', statuses), orderBy('createdAt', 'desc'));
  }

  /**
   * One page of tasks, newest first (issue #358).
   *
   * Replaces getAllTasks(): the Task Management page used to download every
   * task ever created on each load and filter in the browser. Measured at
   * 5,000 tasks that was 5,000 document reads, ~445ms and ~2.14MB per load,
   * growing linearly and without bound.
   */
  async getTasksPage(options: {
    statuses?: TaskStatusFilter;
    pageSize?: number;
    cursor?: unknown | null;
  } = {}): Promise<Page<DeliveryTask>> {
    try {
      this.requireAdminSession('getTasksPage');
      return await this.fetchPage(
        this.taskQueryForStatus(options.statuses ?? 'all'),
        options.pageSize ?? DEFAULT_PAGE_SIZE,
        options.cursor ?? null,
        (d) => this.mapTaskDoc(d)
      );
    } catch (error) {
      console.error('Error fetching task page:', error);
      throw error;
    }
  }

  /**
   * Task counts per status, computed server-side.
   *
   * The stat cards and tab badges previously came from `.filter().length` over
   * the whole downloaded collection. A count query returns one number whatever
   * the collection size, so the cards stay exact while the list stays paged.
   */
  async getTaskStatusCounts(): Promise<{
    all: number;
    active: number;
    completed: number;
    cancelled: number;
  }> {
    this.requireAdminSession('getTaskStatusCounts');
    const tasksRef = collection(db, this.TASKS_COLLECTION);

    const countOf = async (q: Query<DocumentData>): Promise<number> =>
      (await getCountFromServer(q)).data().count;

    const [all, active, completed, cancelled] = await Promise.all([
      countOf(tasksRef),
      countOf(query(tasksRef, where('status', 'in', ACTIVE_TASK_STATUSES))),
      countOf(query(tasksRef, where('status', '==', 'completed'))),
      countOf(query(tasksRef, where('status', '==', 'cancelled'))),
    ]);

    return { all, active, completed, cancelled };
  }

  /**
   * Every task matching a status filter, for CSV export.
   *
   * Deliberately reads more than a page — that is what an export is — but in
   * bounded pages and with a hard stop, and it reports truncation so the
   * operator is never handed a short file that looks complete.
   */
  async getTasksForExport(
    statuses: TaskStatusFilter = 'all'
  ): Promise<{ rows: DeliveryTask[]; truncated: boolean }> {
    this.requireAdminSession('getTasksForExport');
    return this.fetchAllPaged(this.taskQueryForStatus(statuses), (d) => this.mapTaskDoc(d));
  }

  /**
   * The newest `count` tasks (issue #358).
   *
   * The dashboard used to call getAllTasks() and `.slice(0, 5)` — downloading
   * every task ever created to render five rows.
   */
  async getRecentTasks(count: number = 5): Promise<DeliveryTask[]> {
    this.requireAdminSession('getRecentTasks');
    const page = await this.fetchPage(
      this.taskQueryForStatus('all'),
      count,
      null,
      (d) => this.mapTaskDoc(d)
    );
    return page.rows;
  }

  /**
   * Look a task up by its human-facing order id.
   *
   * Firestore has no substring search, so the Task Management search box can
   * only filter the rows already loaded. This gives the one lookup that has to
   * work regardless of what is on screen — pasting an order id from a support
   * ticket — as an indexed equality query rather than a collection scan.
   */
  async findTasksByOrderId(orderId: string): Promise<DeliveryTask[]> {
    this.requireAdminSession('findTasksByOrderId');
    const trimmed = (orderId || '').trim();
    if (!trimmed) return [];

    const tasksRef = collection(db, this.TASKS_COLLECTION);
    const results: DeliveryTask[] = [];

    // Exact order id, then the document id, which support tickets also quote.
    const byOrderId = await getDocs(
      query(tasksRef, where('orderId', '==', trimmed), limit(DEFAULT_PAGE_SIZE))
    );
    results.push(...byOrderId.docs.map((d) => this.mapTaskDoc(d)));

    if (results.length === 0) {
      const byDocId = await getDoc(doc(db, this.TASKS_COLLECTION, trimmed));
      if (byDocId.exists()) {
        results.push(this.mapTaskDoc(byDocId));
      }
    }

    return results;
  }

  /**
   * Issue #369: cancelling or completing a task used to move money with two
   * independent client-side `updateDoc` calls, trusted by the Firestore
   * rules only because `hasAdminRole()` exempts every admin write from the
   * wallet-invariant check. Both transitions now go through a Cloud
   * Function that recomputes the amount server-side and moves the wallet
   * write and the status write in one transaction. Any other status value
   * (there are currently no other callers) keeps the plain client write,
   * since it carries no money movement.
   */
  async updateTaskStatus(taskId: string, status: string): Promise<boolean> {
    try {
      if (status === 'cancelled') {
        const callable = httpsCallable<{ taskId: string }, { success: boolean; refunded: number }>(
          functions,
          'adminCancelTaskWithRefund'
        );
        await callable({ taskId });
        return true;
      }

      if (status === 'completed' || status === 'delivered') {
        const callable = httpsCallable<{ taskId: string }, { success: boolean; payout: number }>(
          functions,
          'completeTaskWithPayment'
        );
        await callable({ taskId });
        return true;
      }

      const taskRef = doc(db, this.TASKS_COLLECTION, taskId);
      await updateDoc(taskRef, {
        status: status,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error updating task status:', error);
      return false;
    }
  }

  // Customer Management

  /**
   * Shape a user document into a Customer row.
   *
   * Addresses and per-customer totals are supplied by the caller: the list view
   * needs totals but not addresses, and the detail dialog needs both. Folding
   * them in here is what produced the read-per-customer fan-out (issue #358).
   */
  private mapCustomerDoc(
    doc: ReadableDoc,
    stats: { totalTasks: number; totalSpent: number },
    addresses: CustomerAddress[] = []
  ): Customer {
    const data = doc.data() || {};

    // Build customer name from profile.firstName and profile.lastName first, then fallback to other fields
    const firstName = data.profile?.firstName || '';
    const lastName = data.profile?.lastName || '';
    const fullNameFromProfile = firstName && lastName
      ? `${firstName} ${lastName}`.trim()
      : firstName || lastName;

    const customerName = fullNameFromProfile || data.name || data.displayName || 'Unknown Customer';

    // Get phone number from users.phone field directly
    const phoneNumber = data.phone || data.phoneNumber || '';

    return {
      id: doc.id,
      ...data,
      // Issue #262: addresses are loaded on demand, not per list row
      addresses,
      // Handle name field consistency - prioritize profile.firstName + profile.lastName
      name: customerName,
      displayName: customerName,
      // Handle phone field consistency - use users.phone directly
      phone: phoneNumber,
      phoneNumber: phoneNumber,
      // Handle email
      email: data.email || '',
      // Issue #239: Handle accountStatus field from mobile app (maps to status)
      status: data.accountStatus || data.status || 'active',
      // Issue #209: stats are derived from the tasks collection, not stored
      totalTasks: stats.totalTasks,
      totalSpent: stats.totalSpent,
      isVerified: data.isVerified || false,
      // Handle dates
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLoginAt: data.lastLoginAt?.toDate() || undefined,
      // Issue #239: Account deletion fields
      deletedAt: data.deletedAt?.toDate() || undefined,
      deletionReason: data.deletionReason || undefined,
      deletionDetails: data.deletionDetails || undefined,
    } as Customer;
  }

  /**
   * Lifetime task count and spend for one customer, computed server-side.
   *
   * Issue #209 derived these from the tasks collection, which was right, but it
   * did so by downloading every task and grouping in the browser. A count and a
   * sum over an indexed equality filter return the same two numbers without
   * reading any task documents.
   */
  private async getCustomerStats(
    userId: string
  ): Promise<{ totalTasks: number; totalSpent: number }> {
    const tasksRef = collection(db, this.TASKS_COLLECTION);
    try {
      const [countSnap, spendSnap] = await Promise.all([
        getCountFromServer(query(tasksRef, where('userId', '==', userId))),
        getAggregateFromServer(
          query(tasksRef, where('userId', '==', userId), where('status', '==', 'completed')),
          { total: sum('finalPrice') }
        ),
      ]);
      return {
        totalTasks: countSnap.data().count,
        totalSpent: spendSnap.data().total || 0,
      };
    } catch (error) {
      console.error(`Error aggregating tasks for customer ${userId}:`, error);
      return { totalTasks: 0, totalSpent: 0 };
    }
  }

  /** Saved addresses for one customer (issue #262), loaded when a row is opened. */
  async getCustomerAddresses(userId: string): Promise<CustomerAddress[]> {
    this.requireAdminSession('getCustomerAddresses');
    try {
      const snapshot = await getDocs(collection(db, this.USERS_COLLECTION, userId, 'addresses'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as CustomerAddress[];
    } catch (error) {
      console.error(`Error fetching addresses for customer ${userId}:`, error);
      return [];
    }
  }

  /**
   * One page of customers, newest first (issue #358).
   *
   * getAllCustomers() read the users collection, the tasks collection, and the
   * addresses subcollection of every user — 5,001 queries for 5,000 customers,
   * on every page load. Totals are now aggregated per visible row and addresses
   * are fetched only when a row is opened.
   */
  async getCustomersPage(options: {
    pageSize?: number;
    cursor?: unknown | null;
  } = {}): Promise<Page<Customer>> {
    this.requireAdminSession('getCustomersPage');

    const usersRef = collection(db, this.USERS_COLLECTION);
    const page = await this.fetchPage(
      query(usersRef, orderBy('createdAt', 'desc')),
      options.pageSize ?? DEFAULT_PAGE_SIZE,
      options.cursor ?? null,
      (d) => d
    );

    const rows = await Promise.all(
      page.rows.map(async (d) => this.mapCustomerDoc(d, await this.getCustomerStats(d.id)))
    );

    return { rows, cursor: page.cursor, hasMore: page.hasMore };
  }

  /** Customer counts per account status, computed server-side. */
  async getCustomerStatusCounts(): Promise<{
    all: number;
    active: number;
    banned: number;
    deleted: number;
  }> {
    this.requireAdminSession('getCustomerStatusCounts');
    const usersRef = collection(db, this.USERS_COLLECTION);

    const countOf = async (q: Query<DocumentData>): Promise<number> =>
      (await getCountFromServer(q)).data().count;

    // Issue #239: the mobile app writes `accountStatus`; older documents use
    // `status`. Count both and treat "neither field set" as active, which is
    // what the row mapper does.
    const [all, bannedA, bannedB, deletedA, deletedB] = await Promise.all([
      countOf(usersRef),
      countOf(query(usersRef, where('accountStatus', '==', 'banned'))),
      countOf(query(usersRef, where('status', '==', 'banned'))),
      countOf(query(usersRef, where('accountStatus', '==', 'deleted'))),
      countOf(query(usersRef, where('status', '==', 'deleted'))),
    ]);

    const banned = Math.max(bannedA, bannedB);
    const deleted = Math.max(deletedA, deletedB);
    return { all, active: Math.max(all - banned - deleted, 0), banned, deleted };
  }

  /** Every customer, for CSV export. Paged and capped like the task export. */
  async getCustomersForExport(): Promise<{ rows: Customer[]; truncated: boolean }> {
    this.requireAdminSession('getCustomersForExport');

    const usersRef = collection(db, this.USERS_COLLECTION);
    const page = await this.fetchAllPaged(
      query(usersRef, orderBy('createdAt', 'desc')),
      (d) => d
    );

    const rows = await Promise.all(
      page.rows.map(async (d) => this.mapCustomerDoc(d, await this.getCustomerStats(d.id)))
    );

    return { rows, truncated: page.truncated };
  }

  async updateCustomerStatus(customerId: string, status: string): Promise<boolean> {
    try {
      const customerRef = doc(db, this.USERS_COLLECTION, customerId);
      await updateDoc(customerRef, {
        status: status,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error updating customer status:', error);
      return false;
    }
  }

  // Settings Management
  async getAppSettings(): Promise<AppSettings | null> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getAppSettings]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getAppSettings]: Admin authenticated:', currentUser.email);
      
      const settingsRef = doc(db, this.SETTINGS_COLLECTION, 'main');
      const docSnapshot = await getDoc(settingsRef);
      
      if (!docSnapshot.exists()) {
        // Create default settings if they don't exist
        const defaultSettings: Omit<AppSettings, 'id'> = {
          generalSettings: {
            companyName: 'PickupNinja',
            supportEmail: 'support@pickupninja.com',
            supportPhone: '+91-8000-000-000',
            maxDeliveryRadius: 50,
            defaultCurrency: 'INR',
            timezone: 'Asia/Kolkata',
            gstNumber: '',
            gstPercentage: 0,
            platformCharge: {
              type: 'percentage',
              value: 5,
            },
            payoutCharge: {
              type: 'flat',
              value: 10,
            },
            maxDeliveryDistance: 50, // km
            supportContact: {
              email: 'support@pickupninja.com',
              phone: '+91-8000-000-000',
            },
          },
          ninjaTravelTime: 30, // minutes
          updatedAt: new Date(),
          updatedBy: 'system',
        };

        await setDoc(settingsRef, {
          ...defaultSettings,
          updatedAt: Timestamp.now(),
        });

        return { id: 'main', ...defaultSettings };
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data(),
        updatedAt: docSnapshot.data()?.updatedAt?.toDate() || new Date(),
      } as AppSettings;
    } catch (error) {
      console.error('Error fetching app settings:', error);
      return null;
    }
  }

  async updateAppSettings(settings: Partial<AppSettings>): Promise<boolean>;
  async updateAppSettings(settings: Partial<AppSettings>, updatedBy: string): Promise<boolean>;
  async updateAppSettings(settings: Partial<AppSettings>, updatedBy: string = 'admin'): Promise<boolean> {
    try {
      const settingsRef = doc(db, this.SETTINGS_COLLECTION, 'main');
      await updateDoc(settingsRef, {
        ...settings,
        updatedAt: Timestamp.now(),
        updatedBy: updatedBy,
      });
      return true;
    } catch (error) {
      console.error('Error updating app settings:', error);
      return false;
    }
  }

  // Service Segments Management
  async getAllSegments(): Promise<ServiceSegment[]> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getAllSegments]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getAllSegments]: Admin authenticated:', currentUser.email);

      // Issue #270: Only fetch active segments (soft delete support)
      const segmentsRef = collection(db, this.SEGMENTS_COLLECTION);
      const q = query(segmentsRef, where('isActive', '==', true), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      console.log(`Found ${querySnapshot.docs.length} segment documents`);
      
      const segments = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }) as ServiceSegment[];

      // Remove duplicates by name (keep the first occurrence by order)
      const uniqueSegments = segments.filter((segment, index, self) => 
        index === self.findIndex(s => s.name === segment.name)
      );

      if (uniqueSegments.length !== segments.length) {
        console.warn(`Found ${segments.length - uniqueSegments.length} duplicate segments, showing unique ones`);
      }

      return uniqueSegments;
    } catch (error) {
      console.error('Error fetching segments:', error);
      return [];
    }
  }

  async createSegment(segment: Omit<ServiceSegment, 'id' | 'createdAt' | 'updatedAt'>): Promise<boolean> {
    try {
      const segmentsRef = collection(db, this.SEGMENTS_COLLECTION);
      const newSegmentRef = doc(segmentsRef);
      
      await setDoc(newSegmentRef, {
        ...segment,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      return true;
    } catch (error) {
      console.error('Error creating segment:', error);
      return false;
    }
  }

  async updateSegment(segmentId: string, updates: Partial<ServiceSegment>): Promise<boolean> {
    try {
      const segmentRef = doc(db, this.SEGMENTS_COLLECTION, segmentId);
      await updateDoc(segmentRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error updating segment:', error);
      return false;
    }
  }

  async deleteSegment(segmentId: string): Promise<boolean> {
    try {
      // Issue #270: Cascade soft delete - Set isActive to false for segment and all associated data
      console.log(`Issue #270: Soft deleting segment ${segmentId} and all associated data...`);

      const now = Timestamp.now();

      // 1. Find and soft delete all categories under this segment
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const categoriesQuery = query(categoriesRef, where('segmentId', '==', segmentId));
      const categoriesSnapshot = await getDocs(categoriesQuery);

      console.log(`Issue #270: Found ${categoriesSnapshot.size} categories to soft delete`);

      // Track category IDs for rate deletion
      const categoryIds = categoriesSnapshot.docs.map(doc => doc.id);

      // Issue #360: this cascade used Promise.all over individual updateDoc
      // calls, which fails fast. One rejected write left the data half-applied:
      // some categories deactivated, others not, and the segment itself still
      // active because step 3 never ran — with only `false` returned to say so.
      //
      // Collect every write first, then commit in atomic batches. Firestore
      // commits a batch all-or-nothing (up to 500 writes), so a failure can no
      // longer deactivate half the children.
      //
      // The segment is written LAST on purpose. If a batch fails, the segment
      // stays active with some children deactivated — visibly incomplete and
      // safe to retry. The reverse order would hide an active child under a
      // deleted parent.
      const writes: Array<{ ref: any; data: any }> = [];

      for (const categoryDoc of categoriesSnapshot.docs) {
        writes.push({ ref: categoryDoc.ref, data: { isActive: false, updatedAt: now } });
      }

      // Find the rates under these categories. Firestore 'in' takes 10 at a time.
      let totalRatesSoftDeleted = 0;
      if (categoryIds.length > 0) {
        const ratesRef = collection(db, this.RATES_COLLECTION);
        const lookupSize = 10;

        for (let i = 0; i < categoryIds.length; i += lookupSize) {
          const slice = categoryIds.slice(i, i + lookupSize);
          const ratesSnapshot = await getDocs(query(ratesRef, where('categoryId', 'in', slice)));
          for (const rateDoc of ratesSnapshot.docs) {
            writes.push({ ref: rateDoc.ref, data: { isActive: false, updatedAt: now } });
          }
          totalRatesSoftDeleted += ratesSnapshot.size;
        }
      }

      // The segment itself, last.
      writes.push({
        ref: doc(db, this.SEGMENTS_COLLECTION, segmentId),
        data: { isActive: false, updatedAt: now },
      });

      const BATCH_LIMIT = 500;
      let committed = 0;

      for (let i = 0; i < writes.length; i += BATCH_LIMIT) {
        const chunk = writes.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach(({ ref, data }) => batch.update(ref, data));

        try {
          await batch.commit();
          committed += chunk.length;
        } catch (batchError) {
          // Say exactly how far it got. Silently returning false here is what
          // made the old partial state impossible to reason about.
          console.error(
            `❌ Issue #270: cascade failed after ${committed}/${writes.length} writes ` +
              `for segment ${segmentId}. Earlier batches are committed; the segment ` +
              `itself is NOT deactivated, so re-running is safe.`,
            batchError
          );
          return false;
        }
      }

      console.log(`Issue #270: Soft deleted ${totalRatesSoftDeleted} rates`);

      console.log(`✅ Issue #270: Successfully soft deleted segment ${segmentId} with ${categoryIds.length} categories`);
      return true;
    } catch (error) {
      console.error('❌ Issue #270: Error soft deleting segment with cascade:', error);
      return false;
    }
  }

  async cleanupDuplicateSegments(): Promise<{ success: boolean; duplicatesRemoved: number; details: string[] }> {
    try {
      console.log('Starting cleanup of duplicate segments...');
      
      const segmentsRef = collection(db, this.SEGMENTS_COLLECTION);
      // Get all segments without ordering to avoid composite index requirement
      const querySnapshot = await getDocs(segmentsRef);
      
      console.log(`Found ${querySnapshot.docs.length} total segment documents`);
      
      const segments = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Handle both Timestamp and Date objects for createdAt
        let createdAt = new Date();
        if (data.createdAt) {
          if (typeof data.createdAt.toDate === 'function') {
            // Firestore Timestamp
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            // Already a Date object
            createdAt = data.createdAt;
          } else {
            // String or other format
            createdAt = new Date(data.createdAt);
          }
        }
        
        return {
          id: doc.id,
          name: data.name,
          order: data.order || 0,
          createdAt: createdAt,
          ...data,
        };
      });

      // Group segments by name to identify duplicates
      const segmentGroups = new Map<string, typeof segments>();
      segments.forEach(segment => {
        const name = segment.name;
        if (!segmentGroups.has(name)) {
          segmentGroups.set(name, []);
        }
        segmentGroups.get(name)!.push(segment);
      });

      const duplicatesToDelete: string[] = [];
      const details: string[] = [];

      // For each group, keep the first one (by order, then by creation date) and mark others for deletion
      segmentGroups.forEach((group, name) => {
        if (group.length > 1) {
          // Sort by order first, then by creation date
          group.sort((a, b) => {
            // Compare order first
            const orderA = typeof a.order === 'number' ? a.order : 0;
            const orderB = typeof b.order === 'number' ? b.order : 0;
            if (orderA !== orderB) return orderA - orderB;
            
            // Then compare creation date
            const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
            const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
            return timeA - timeB;
          });
          
          const keepSegment = group[0];
          const deleteSegments = group.slice(1);
          
          console.log(`Found ${group.length} segments with name "${name}"`);
          console.log(`Keeping: ${keepSegment.id} (order: ${keepSegment.order}, created: ${keepSegment.createdAt instanceof Date ? keepSegment.createdAt.toISOString() : 'unknown'})`);
          
          deleteSegments.forEach(segment => {
            duplicatesToDelete.push(segment.id);
            console.log(`Marking for deletion: ${segment.id} (order: ${segment.order}, created: ${segment.createdAt instanceof Date ? segment.createdAt.toISOString() : 'unknown'})`);
            details.push(`Removed duplicate "${name}" (ID: ${segment.id})`);
          });
        }
      });

      // Delete duplicates
      console.log(`Deleting ${duplicatesToDelete.length} duplicate segments...`);
      for (const segmentId of duplicatesToDelete) {
        try {
          await this.deleteSegment(segmentId);
          console.log(`Successfully deleted segment: ${segmentId}`);
        } catch (error) {
          console.error(`Failed to delete segment ${segmentId}:`, error);
          details.push(`Failed to delete segment ${segmentId}: ${error}`);
        }
      }

      const result = {
        success: true,
        duplicatesRemoved: duplicatesToDelete.length,
        details
      };

      console.log(`Cleanup completed. Removed ${duplicatesToDelete.length} duplicate segments`);
      return result;
    } catch (error) {
      console.error('Error cleaning up duplicate segments:', error);
      return {
        success: false,
        duplicatesRemoved: 0,
        details: [`Error: ${error}`]
      };
    }
  }

  async cleanupDuplicateCategories(): Promise<{ success: boolean; duplicatesRemoved: number; details: string[] }> {
    try {
      console.log('Starting cleanup of duplicate categories...');
      
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const querySnapshot = await getDocs(categoriesRef);
      
      console.log(`Found ${querySnapshot.docs.length} total category documents`);
      
      const categories = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Handle both Timestamp and Date objects for createdAt
        let createdAt = new Date();
        if (data.createdAt) {
          if (typeof data.createdAt.toDate === 'function') {
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAt = data.createdAt;
          } else {
            createdAt = new Date(data.createdAt);
          }
        }
        
        return {
          id: doc.id,
          name: data.name,
          segmentId: data.segmentId,
          createdAt: createdAt,
          ...data,
        };
      });

      // Group categories by name AND segmentId (since categories can have same name in different segments)
      const categoryGroups = new Map<string, typeof categories>();
      categories.forEach(category => {
        const key = `${category.segmentId}-${category.name}`;
        if (!categoryGroups.has(key)) {
          categoryGroups.set(key, []);
        }
        categoryGroups.get(key)!.push(category);
      });

      const duplicatesToDelete: string[] = [];
      const details: string[] = [];

      // For each group, keep the first one (by creation date) and mark others for deletion
      categoryGroups.forEach((group, key) => {
        if (group.length > 1) {
          // Sort by creation date
          group.sort((a, b) => {
            const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
            const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
            return timeA - timeB;
          });
          
          const keepCategory = group[0];
          const deleteCategories = group.slice(1);
          
          console.log(`Found ${group.length} categories with key "${key}"`);
          console.log(`Keeping: ${keepCategory.id} (created: ${keepCategory.createdAt instanceof Date ? keepCategory.createdAt.toISOString() : 'unknown'})`);
          
          deleteCategories.forEach(category => {
            duplicatesToDelete.push(category.id);
            console.log(`Marking for deletion: ${category.id} (created: ${category.createdAt instanceof Date ? category.createdAt.toISOString() : 'unknown'})`);
            details.push(`Removed duplicate "${category.name}" in segment ${category.segmentId} (ID: ${category.id})`);
          });
        }
      });

      // Delete duplicates
      console.log(`Deleting ${duplicatesToDelete.length} duplicate categories...`);
      for (const categoryId of duplicatesToDelete) {
        try {
          await this.deleteCategory(categoryId);
          console.log(`Successfully deleted category: ${categoryId}`);
        } catch (error) {
          console.error(`Failed to delete category ${categoryId}:`, error);
          details.push(`Failed to delete category ${categoryId}: ${error}`);
        }
      }

      const result = {
        success: true,
        duplicatesRemoved: duplicatesToDelete.length,
        details
      };

      console.log(`Categories cleanup completed. Removed ${duplicatesToDelete.length} duplicate categories`);
      return result;
    } catch (error) {
      console.error('Error cleaning up duplicate categories:', error);
      return {
        success: false,
        duplicatesRemoved: 0,
        details: [`Error: ${error}`]
      };
    }
  }

  async cleanupAllDuplicates(): Promise<{ success: boolean; segmentDuplicatesRemoved: number; categoryDuplicatesRemoved: number; details: string[] }> {
    try {
      console.log('Starting comprehensive cleanup of all duplicates...');
      
      // Clean up segments first
      const segmentResult = await this.cleanupDuplicateSegments();
      
      // Clean up categories
      const categoryResult = await this.cleanupDuplicateCategories();
      
      const combinedDetails = [
        `Segments: ${segmentResult.duplicatesRemoved} duplicates removed`,
        `Categories: ${categoryResult.duplicatesRemoved} duplicates removed`,
        ...segmentResult.details,
        ...categoryResult.details
      ];
      
      return {
        success: segmentResult.success && categoryResult.success,
        segmentDuplicatesRemoved: segmentResult.duplicatesRemoved,
        categoryDuplicatesRemoved: categoryResult.duplicatesRemoved,
        details: combinedDetails
      };
    } catch (error) {
      console.error('Error during comprehensive cleanup:', error);
      return {
        success: false,
        segmentDuplicatesRemoved: 0,
        categoryDuplicatesRemoved: 0,
        details: [`Error: ${error}`]
      };
    }
  }

  // Service Categories Management
  async getAllCategories(): Promise<ServiceCategory[]> {
    try {
      // Issue #270: Only fetch active categories (soft delete support)
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const q = query(categoriesRef, where('isActive', '==', true), orderBy('segmentId', 'asc'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      console.log(`Found ${querySnapshot.docs.length} category documents`);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }) as ServiceCategory[];
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  async createCategory(category: { segmentId: string; name: string; icon: string; description: string; isActive: boolean }): Promise<boolean> {
    try {
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const newCategoryRef = doc(categoriesRef);
      
      await setDoc(newCategoryRef, {
        ...category,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      return true;
    } catch (error) {
      console.error('Error creating category:', error);
      return false;
    }
  }

  async updateCategory(categoryId: string, updates: any): Promise<boolean> {
    try {
      const categoryRef = doc(db, this.CATEGORIES_COLLECTION, categoryId);
      await updateDoc(categoryRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error updating category:', error);
      return false;
    }
  }

  async deleteCategory(categoryId: string): Promise<boolean> {
    try {
      // Issue #270: Soft delete - set isActive to false instead of permanent deletion
      const categoryRef = doc(db, this.CATEGORIES_COLLECTION, categoryId);
      await updateDoc(categoryRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
      console.log(`✅ Issue #270: Category ${categoryId} soft deleted (isActive: false)`);
      return true;
    } catch (error) {
      console.error('❌ Issue #270: Error soft deleting category:', error);
      return false;
    }
  }

  // Service Rates Management
  async createServiceRate(rate: CreateServiceRateRequest): Promise<boolean> {
    try {
      const ratesRef = collection(db, this.RATES_COLLECTION);
      const newRateRef = doc(ratesRef);
      
      // Normalize taskType
      const normalizeTaskType = (input?: string): 'delivery' | 'service' | undefined => {
        if (!input) return undefined;
        const normalized = input.toLowerCase().trim();
        if (normalized.includes('delivery') || normalized === 'delivery') {
          return 'delivery';
        } else if (normalized.includes('service') || normalized === 'service') {
          return 'service';
        }
        return undefined;
      };
      
      // Filter out undefined values to prevent Firebase errors
      const cleanRate: any = {
        categoryId: rate.categoryId,
        serviceName: rate.serviceName,
        basePrice: rate.basePrice, // For delivery services - multiplies with pickup/drop points
        urgencyMultipliers: rate.urgencyMultipliers,
        isActive: rate.isActive,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Only add optional fields if they have values
      if (rate.pricePerUnit !== undefined) {
        cleanRate.pricePerUnit = rate.pricePerUnit;
      }
      if (rate.unit !== undefined) {
        cleanRate.unit = rate.unit;
      }
      if (rate.maxWeight !== undefined) {
        cleanRate.maxWeight = rate.maxWeight;
      }
      if (rate.durationInMinutes !== undefined) {
        cleanRate.durationInMinutes = rate.durationInMinutes;
      }
      if (rate.urgencyTimes !== undefined) {
        cleanRate.urgencyTimes = rate.urgencyTimes;
      }
      if (rate.taskType !== undefined) {
        const normalizedTaskType = normalizeTaskType(rate.taskType as any);
        if (normalizedTaskType) {
          cleanRate.taskType = normalizedTaskType;
        }
      }
      const imageUrl = normalizeImageUrl(rate.imageUrl);
      if (imageUrl) {
        const imageError = validateImageUrl(imageUrl);
        if (imageError) throw new Error(imageError);
        cleanRate.imageUrl = imageUrl;
        const sourceUrl = normalizeImageUrl(rate.imageSourceUrl);
        if (sourceUrl) cleanRate.imageSourceUrl = sourceUrl;
      }
      if (rate.subscriptionEnabled) {
        cleanRate.subscriptionEnabled = true;
        if (rate.subscriptionPrice && rate.subscriptionPrice > 0) cleanRate.subscriptionPrice = rate.subscriptionPrice;
      }
      
      await setDoc(newRateRef, cleanRate);
      
      return true;
    } catch (error) {
      console.error('Error creating service rate:', error);
      return false;
    }
  }

  async updateServiceRate(rateId: string, updates: Partial<CreateServiceRateRequest>): Promise<boolean> {
    try {
      // Normalize taskType if present
      const normalizeTaskType = (input?: string): 'delivery' | 'service' | undefined => {
        if (!input) return undefined;
        const normalized = input.toLowerCase().trim();
        if (normalized.includes('delivery') || normalized === 'delivery') {
          return 'delivery';
        } else if (normalized.includes('service') || normalized === 'service') {
          return 'service';
        }
        return undefined;
      };
      
      const cleanUpdates: any = { ...updates };
      
      // Normalize taskType if it exists in updates
      if (updates.taskType !== undefined) {
        const normalizedTaskType = normalizeTaskType(updates.taskType as any);
        if (normalizedTaskType) {
          cleanUpdates.taskType = normalizedTaskType;
        } else {
          // Remove taskType if normalization fails
          delete cleanUpdates.taskType;
        }
      }
      
      // An emptied image field removes the image rather than storing "".
      if (updates.imageUrl !== undefined) {
        const imageUrl = normalizeImageUrl(updates.imageUrl);
        if (imageUrl) {
          const imageError = validateImageUrl(imageUrl);
          if (imageError) throw new Error(imageError);
          cleanUpdates.imageUrl = imageUrl;
          const sourceUrl = normalizeImageUrl(updates.imageSourceUrl);
          cleanUpdates.imageSourceUrl = sourceUrl || deleteField();
        } else {
          cleanUpdates.imageUrl = deleteField();
          cleanUpdates.imageSourceUrl = deleteField();
        }
      }

      // The subscription price only exists while subscriptions are on; an
      // emptied price falls back to the base price rather than storing 0.
      if ('subscriptionEnabled' in updates || 'subscriptionPrice' in updates) {
        const enabled = !!updates.subscriptionEnabled;
        const price = updates.subscriptionPrice;
        cleanUpdates.subscriptionEnabled = enabled;
        cleanUpdates.subscriptionPrice = enabled && price && price > 0 ? price : deleteField();
      }
      
      const rateRef = doc(db, this.RATES_COLLECTION, rateId);
      await updateDoc(rateRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error updating service rate:', error);
      return false;
    }
  }

  /**
   * Copies an image from any public https link into our own Storage via the
   * importServiceImage function, so a service photo doesn't depend on a
   * third-party host staying up. Returns the stored copy's URL.
   * Throws with an admin-readable message on failure.
   */
  async importServiceImage(url: string): Promise<{ url: string; sourceUrl: string; reused: boolean }> {
    const callable = httpsCallable<{ url: string }, { url: string; sourceUrl: string; reused: boolean }>(
      functions,
      'importServiceImage'
    );
    try {
      const result = await callable({ url });
      return result.data;
    } catch (error: any) {
      // HttpsError messages from this function are written for admins.
      const message =
        error?.code === 'functions/invalid-argument' && error?.message
          ? error.message
          : "Couldn't copy the image into storage — try again, or use a different link";
      throw new Error(message);
    }
  }

  async deleteServiceRate(rateId: string): Promise<boolean> {
    try {
      // Issue #270: Soft delete - set isActive to false instead of permanent deletion
      const rateRef = doc(db, this.RATES_COLLECTION, rateId);
      await updateDoc(rateRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
      console.log(`✅ Issue #270: Service rate ${rateId} soft deleted (isActive: false)`);
      return true;
    } catch (error) {
      console.error('❌ Issue #270: Error soft deleting service rate:', error);
      return false;
    }
  }

  async getAllServiceRates(): Promise<ServiceRate[]> {
    try {
      // Issue #270: Only fetch active service rates (soft delete support)
      const ratesRef = collection(db, this.RATES_COLLECTION);
      const q = query(ratesRef, where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as ServiceRate;
      });
    } catch (error) {
      console.error('Error fetching service rates:', error);
      return [];
    }
  }

  // Banner Management
  async getAllBanners(): Promise<Banner[]> {
    try {
      // Issue #271: Only fetch active banners (soft delete support)
      const bannersRef = collection(db, this.BANNERS_COLLECTION);
      const q = query(bannersRef, where('isActive', '==', true), orderBy('priority', 'desc'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      console.log(`Found ${querySnapshot.docs.length} banner documents`);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }) as Banner[];
    } catch (error) {
      console.error('Error fetching banners:', error);
      return [];
    }
  }

  async getActiveBanners(segmentId?: string, categoryId?: string): Promise<Banner[]> {
    try {
      const bannersRef = collection(db, this.BANNERS_COLLECTION);
      const now = new Date();
      
      let q = query(
        bannersRef,
        where('isActive', '==', true),
        where('startDate', '<=', Timestamp.fromDate(now)),
        where('endDate', '>=', Timestamp.fromDate(now)),
        orderBy('priority', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      let banners = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      }) as Banner[];

      // Filter by segment and category if provided
      if (segmentId || categoryId) {
        banners = banners.filter(banner => {
          const segmentMatch = !banner.targetSegmentId || banner.targetSegmentId === segmentId;
          const categoryMatch = !banner.targetCategoryId || banner.targetCategoryId === categoryId;
          return segmentMatch && categoryMatch;
        });
      }

      return banners;
    } catch (error) {
      console.error('Error fetching active banners:', error);
      return [];
    }
  }

  async createBanner(banner: Omit<Banner, 'id' | 'createdAt' | 'updatedAt' | 'clickCount' | 'impressionCount' | 'budgetSpent'>): Promise<boolean> {
    try {
      const bannersRef = collection(db, this.BANNERS_COLLECTION);
      const newBannerRef = doc(bannersRef);
      
      await setDoc(newBannerRef, {
        ...banner,
        clickCount: 0,
        impressionCount: 0,
        budgetSpent: 0,
        startDate: Timestamp.fromDate(banner.startDate),
        endDate: Timestamp.fromDate(banner.endDate),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      return true;
    } catch (error) {
      console.error('Error creating banner:', error);
      return false;
    }
  }

  async updateBanner(bannerId: string, updates: Partial<Banner>): Promise<boolean> {
    try {
      const bannerRef = doc(db, this.BANNERS_COLLECTION, bannerId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      // Handle date fields
      if (updates.startDate) {
        updateData.startDate = Timestamp.fromDate(updates.startDate);
      }
      if (updates.endDate) {
        updateData.endDate = Timestamp.fromDate(updates.endDate);
      }

      await updateDoc(bannerRef, updateData);
      return true;
    } catch (error) {
      console.error('Error updating banner:', error);
      return false;
    }
  }

  async deleteBanner(bannerId: string): Promise<boolean> {
    try {
      // Issue #271: Soft delete - set isActive to false instead of permanent deletion
      const bannerRef = doc(db, this.BANNERS_COLLECTION, bannerId);
      await updateDoc(bannerRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
      console.log(`✅ Issue #271: Banner ${bannerId} soft deleted (isActive: false)`);
      return true;
    } catch (error) {
      console.error('❌ Issue #271: Error soft deleting banner:', error);
      return false;
    }
  }

  async incrementBannerImpression(bannerId: string): Promise<boolean> {
    try {
      const bannerRef = doc(db, this.BANNERS_COLLECTION, bannerId);
      const bannerDoc = await getDoc(bannerRef);
      
      if (bannerDoc.exists()) {
        const currentImpressions = bannerDoc.data().impressionCount || 0;
        await updateDoc(bannerRef, {
          impressionCount: currentImpressions + 1,
          updatedAt: Timestamp.now(),
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error incrementing banner impression:', error);
      return false;
    }
  }

  async incrementBannerClick(bannerId: string): Promise<boolean> {
    try {
      const bannerRef = doc(db, this.BANNERS_COLLECTION, bannerId);
      const bannerDoc = await getDoc(bannerRef);
      
      if (bannerDoc.exists()) {
        const currentClicks = bannerDoc.data().clickCount || 0;
        await updateDoc(bannerRef, {
          clickCount: currentClicks + 1,
          updatedAt: Timestamp.now(),
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error incrementing banner click:', error);
      return false;
    }
  }

  // Duplicate Detection for UI Display
  async detectDuplicateSegments(): Promise<{ duplicates: { name: string; count: number; ids: string[] }[]; totalDuplicates: number }> {
    try {
      const segmentsRef = collection(db, this.SEGMENTS_COLLECTION);
      const querySnapshot = await getDocs(segmentsRef);
      
      const segments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        ...doc.data()
      }));

      // Group segments by name to find duplicates
      const segmentGroups = new Map<string, typeof segments>();
      segments.forEach(segment => {
        const name = segment.name;
        if (!segmentGroups.has(name)) {
          segmentGroups.set(name, []);
        }
        segmentGroups.get(name)!.push(segment);
      });

      const duplicates: { name: string; count: number; ids: string[] }[] = [];
      let totalDuplicates = 0;

      segmentGroups.forEach((group, name) => {
        if (group.length > 1) {
          duplicates.push({
            name: name,
            count: group.length,
            ids: group.map(s => s.id)
          });
          totalDuplicates += group.length - 1; // Subtract 1 because one is the original
        }
      });

      return { duplicates, totalDuplicates };
    } catch (error) {
      console.error('Error detecting duplicate segments:', error);
      return { duplicates: [], totalDuplicates: 0 };
    }
  }

  async detectDuplicateCategories(): Promise<{ duplicates: { name: string; segmentId: string; count: number; ids: string[] }[]; totalDuplicates: number }> {
    try {
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const querySnapshot = await getDocs(categoriesRef);
      
      const categories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        segmentId: doc.data().segmentId,
        ...doc.data()
      }));

      // Group categories by segmentId + name to find duplicates
      const categoryGroups = new Map<string, typeof categories>();
      categories.forEach(category => {
        const key = `${category.segmentId}-${category.name}`;
        if (!categoryGroups.has(key)) {
          categoryGroups.set(key, []);
        }
        categoryGroups.get(key)!.push(category);
      });

      const duplicates: { name: string; segmentId: string; count: number; ids: string[] }[] = [];
      let totalDuplicates = 0;

      categoryGroups.forEach((group, key) => {
        if (group.length > 1) {
          duplicates.push({
            name: group[0].name,
            segmentId: group[0].segmentId,
            count: group.length,
            ids: group.map(c => c.id)
          });
          totalDuplicates += group.length - 1; // Subtract 1 because one is the original
        }
      });

      return { duplicates, totalDuplicates };
    } catch (error) {
      console.error('Error detecting duplicate categories:', error);
      return { duplicates: [], totalDuplicates: 0 };
    }
  }

  // Database Reset and Cleanup
  async clearAllCategories(): Promise<boolean> {
    try {
      console.log('Clearing all categories from database...');
      const categoriesRef = collection(db, this.CATEGORIES_COLLECTION);
      const querySnapshot = await getDocs(categoriesRef);
      
      console.log(`Found ${querySnapshot.docs.length} categories to delete`);
      
      for (const doc of querySnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted category: ${doc.id}`);
      }
      
      console.log('All categories cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing categories:', error);
      return false;
    }
  }

  async cleanupOrphanedCategories(): Promise<{ success: boolean; orphansRemoved: number; details: string[] }> {
    try {
      console.log('Starting cleanup of orphaned categories...');
      
      // Get all segments and categories
      const [segments, categories] = await Promise.all([
        this.getAllSegments(),
        this.getAllCategories()
      ]);
      
      console.log(`Found ${segments.length} segments and ${categories.length} categories`);
      
      // Get valid segment IDs
      const validSegmentIds = new Set(segments.map(s => s.id));
      
      // Find orphaned categories
      const orphanedCategories = categories.filter(category => 
        !validSegmentIds.has(category.segmentId)
      );
      
      console.log(`Found ${orphanedCategories.length} orphaned categories`);
      
      const details: string[] = [];
      
      if (orphanedCategories.length === 0) {
        details.push('No orphaned categories found');
        return { success: true, orphansRemoved: 0, details };
      }
      
      // Delete orphaned categories
      for (const category of orphanedCategories) {
        try {
          await this.deleteCategory(category.id);
          details.push(`Removed orphaned category: "${category.name}" (segment ID: ${category.segmentId})`);
          console.log(`Deleted orphaned category: ${category.name} (ID: ${category.id})`);
        } catch (error) {
          console.error(`Failed to delete category ${category.id}:`, error);
          details.push(`Failed to remove category: "${category.name}"`);
        }
      }
      
      console.log(`Cleanup completed. Removed ${orphanedCategories.length} orphaned categories`);
      return { 
        success: true, 
        orphansRemoved: orphanedCategories.length, 
        details 
      };
      
    } catch (error) {
      console.error('Error during orphaned categories cleanup:', error);
      return {
        success: false,
        orphansRemoved: 0,
        details: [`Error during cleanup: ${error}`]
      };
    }
  }

  async clearAllServiceRates(): Promise<boolean> {
    try {
      console.log('Clearing all service rates from database...');
      const ratesRef = collection(db, this.RATES_COLLECTION);
      const querySnapshot = await getDocs(ratesRef);
      
      console.log(`Found ${querySnapshot.docs.length} service rates to delete`);
      
      for (const doc of querySnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted service rate: ${doc.id}`);
      }
      
      console.log('All service rates cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing service rates:', error);
      return false;
    }
  }

  async cleanupOrphanedServiceRates(): Promise<{ success: boolean; orphansRemoved: number; details: string[] }> {
    try {
      console.log('Starting cleanup of orphaned service rates...');
      
      // Get all categories and service rates
      const [categories, serviceRates] = await Promise.all([
        this.getAllCategories(),
        this.getAllServiceRates()
      ]);
      
      console.log(`Found ${categories.length} categories and ${serviceRates.length} service rates`);
      
      // Get valid category IDs
      const validCategoryIds = new Set(categories.map(c => c.id));
      
      // Find orphaned service rates
      const orphanedServiceRates = serviceRates.filter(rate => 
        !validCategoryIds.has(rate.categoryId)
      );
      
      console.log(`Found ${orphanedServiceRates.length} orphaned service rates`);
      
      const details: string[] = [];
      
      if (orphanedServiceRates.length === 0) {
        details.push('No orphaned service rates found');
        return { success: true, orphansRemoved: 0, details };
      }
      
      // Delete orphaned service rates
      for (const rate of orphanedServiceRates) {
        try {
          await this.deleteServiceRate(rate.id);
          details.push(`Removed orphaned service rate: "${rate.serviceName}" (category ID: ${rate.categoryId})`);
          console.log(`Deleted orphaned service rate: ${rate.serviceName} (ID: ${rate.id})`);
        } catch (error) {
          console.error(`Failed to delete service rate ${rate.id}:`, error);
          details.push(`Failed to remove service rate: "${rate.serviceName}"`);
        }
      }
      
      console.log(`Cleanup completed. Removed ${orphanedServiceRates.length} orphaned service rates`);
      return { 
        success: true, 
        orphansRemoved: orphanedServiceRates.length, 
        details 
      };
      
    } catch (error) {
      console.error('Error during orphaned service rates cleanup:', error);
      return {
        success: false,
        orphansRemoved: 0,
        details: [`Error during cleanup: ${error}`]
      };
    }
  }

  async cleanupAllOrphanedData(): Promise<{ success: boolean; categoriesRemoved: number; serviceRatesRemoved: number; details: string[] }> {
    try {
      console.log('Starting comprehensive cleanup of orphaned data...');
      
      const details: string[] = [];
      
      // Clean up orphaned service rates first (they depend on categories)
      const serviceRatesResult = await this.cleanupOrphanedServiceRates();
      details.push(...serviceRatesResult.details);
      
      // Then clean up orphaned categories
      const categoriesResult = await this.cleanupOrphanedCategories();
      details.push(...categoriesResult.details);
      
      const success = serviceRatesResult.success && categoriesResult.success;
      
      console.log(`Comprehensive cleanup completed. Removed ${serviceRatesResult.orphansRemoved} service rates and ${categoriesResult.orphansRemoved} categories`);
      
      return {
        success,
        categoriesRemoved: categoriesResult.orphansRemoved,
        serviceRatesRemoved: serviceRatesResult.orphansRemoved,
        details
      };
      
    } catch (error) {
      console.error('Error during comprehensive cleanup:', error);
      return {
        success: false,
        categoriesRemoved: 0,
        serviceRatesRemoved: 0,
        details: [`Error during cleanup: ${error}`]
      };
    }
  }

  async clearAllSegments(): Promise<boolean> {
    try {
      console.log('Clearing all segments from database...');
      const segmentsRef = collection(db, this.SEGMENTS_COLLECTION);
      const querySnapshot = await getDocs(segmentsRef);
      
      console.log(`Found ${querySnapshot.docs.length} segments to delete`);
      
      for (const doc of querySnapshot.docs) {
        await deleteDoc(doc.ref);
        console.log(`Deleted segment: ${doc.id}`);
      }
      
      console.log('All segments cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing segments:', error);
      return false;
    }
  }

  async resetDatabase(): Promise<{ success: boolean; details: string[] }> {
    try {
      console.log('🔄 Starting complete database reset...');
      const details: string[] = [];
      
      // Clear all service rates first (they depend on categories)
      const ratesCleared = await this.clearAllServiceRates();
      if (ratesCleared) {
        details.push('✅ Service rates cleared');
      } else {
        details.push('❌ Failed to clear service rates');
      }
      
      // Clear all categories (they depend on segments)
      const categoriesCleared = await this.clearAllCategories();
      if (categoriesCleared) {
        details.push('✅ Categories cleared');
      } else {
        details.push('❌ Failed to clear categories');
      }
      
      // Clear all segments
      const segmentsCleared = await this.clearAllSegments();
      if (segmentsCleared) {
        details.push('✅ Segments cleared');
      } else {
        details.push('❌ Failed to clear segments');
      }
      
      // Database cleared - no data initialization
      details.push('✅ Database completely cleared');
      
      const success = ratesCleared && categoriesCleared && segmentsCleared;
      
      console.log(`🏁 Database reset ${success ? 'completed successfully' : 'completed with errors'}`);
      return { success, details };
    } catch (error) {
      console.error('Error during database reset:', error);
      return {
        success: false,
        details: [`❌ Database reset failed: ${error}`]
      };
    }
  }

  // Initialize Default Data
  async initializeDefaultData(): Promise<boolean> {
    try {
      // Check if all data already exists
      const [existingSegments, existingCategories, existingRates] = await Promise.all([
        this.getAllSegments(),
        this.getAllCategories(),
        this.getAllServiceRates()
      ]);
      
      if (existingSegments.length > 0 && existingCategories.length > 0 && existingRates.length > 0) {
        console.log('All data already exists, skipping initialization');
        return true;
      }
      
      console.log('Some data missing, checking what to initialize...', {
        segments: existingSegments.length,
        categories: existingCategories.length,
        rates: existingRates.length
      });

      let segmentIds: string[] = [];

      // Only create segments if they don't exist
      if (existingSegments.length === 0) {
        console.log('Creating default segments...');
        const defaultSegments = [
          { name: 'Delivery', icon: 'LocalShipping', description: 'Send packages & documents', color: '#FF6B35', isActive: true, order: 1 },
          { name: 'Home Services', icon: 'Build', description: 'Repairs & maintenance', color: '#38A169', isActive: true, order: 2 },
          { name: 'Personal Care', icon: 'AcUnit', description: 'Beauty & wellness', color: '#3182CE', isActive: true, order: 3 },
          { name: 'Emergency', icon: 'ElectricalServices', description: 'Urgent assistance', color: '#E53E3E', isActive: true, order: 4 },
        ];

        // Create segments and store their IDs
        for (const segment of defaultSegments) {
          const segmentRef = doc(collection(db, this.SEGMENTS_COLLECTION));
          await setDoc(segmentRef, {
            ...segment,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
          segmentIds.push(segmentRef.id);
        }
      } else {
        console.log('Segments already exist, using existing ones');
        segmentIds = existingSegments.map(s => s.id);
      }

      let categoryIds: string[] = [];

      // Only create categories if they don't exist
      if (existingCategories.length === 0) {
        console.log('Creating default categories...');
        const defaultCategories = [
          // Home Services categories
          { segmentId: segmentIds[1], name: 'Electrical Services', icon: 'ElectricalServices', description: 'Electrical repairs and installations', isActive: true },
          { segmentId: segmentIds[1], name: 'Plumbing Services', icon: 'Plumbing', description: 'Plumbing repairs and maintenance', isActive: true },
          { segmentId: segmentIds[1], name: 'AC Services', icon: 'AcUnit', description: 'Air conditioning repair and maintenance', isActive: true },
          // Personal Care categories
          { segmentId: segmentIds[2], name: 'Beauty Services', icon: 'ContentCut', description: 'Beauty and wellness services', isActive: true },
          { segmentId: segmentIds[2], name: 'Spa Services', icon: 'Spa', description: 'Spa and relaxation services', isActive: true },
          // Emergency categories
          { segmentId: segmentIds[3], name: 'Emergency Repair', icon: 'ElectricalServices', description: 'Emergency home repairs', isActive: true },
          { segmentId: segmentIds[3], name: 'Emergency Delivery', icon: 'LocalShipping', description: 'Urgent delivery services', isActive: true },
        ];

        // Create categories and store their IDs
        for (const category of defaultCategories) {
          const categoryRef = doc(collection(db, this.CATEGORIES_COLLECTION));
          await setDoc(categoryRef, {
            ...category,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
          categoryIds.push(categoryRef.id);
        }
      } else {
        console.log('Categories already exist, using existing ones');
        categoryIds = existingCategories.map(c => c.id);
      }

      // Only create service rates if they don't exist
      if (existingRates.length === 0) {
        console.log('Creating default service rates...');
        const defaultServiceRates = [
          // Electrical Services
          { categoryId: categoryIds[0], serviceName: 'Basic Electrical Repair', basePrice: 200, pricePerUnit: 100, unit: 'hour', urgencyMultipliers: { standard: 1.0, express: 1.3, urgent: 1.8 }, isActive: true },
          { categoryId: categoryIds[0], serviceName: 'Wiring Installation', basePrice: 500, pricePerUnit: 150, unit: 'hour', urgencyMultipliers: { standard: 1.0, express: 1.3, urgent: 1.8 }, isActive: true },
          // Plumbing Services
          { categoryId: categoryIds[1], serviceName: 'Pipe Repair', basePrice: 300, pricePerUnit: 80, unit: 'hour', urgencyMultipliers: { standard: 1.0, express: 1.4, urgent: 1.9 }, isActive: true },
          { categoryId: categoryIds[1], serviceName: 'Toilet Installation', basePrice: 800, pricePerUnit: 200, unit: 'service', urgencyMultipliers: { standard: 1.0, express: 1.4, urgent: 1.9 }, isActive: true },
          // AC Services
          { categoryId: categoryIds[2], serviceName: 'AC Servicing', basePrice: 500, pricePerUnit: 300, unit: 'service', urgencyMultipliers: { standard: 1.0, express: 1.2, urgent: 1.6 }, isActive: true },
          // Beauty Services
          { categoryId: categoryIds[3], serviceName: 'Hair Cut & Style', basePrice: 300, pricePerUnit: 0, unit: 'service', urgencyMultipliers: { standard: 1.0, express: 1.2, urgent: 1.5 }, isActive: true },
          // Spa Services
          { categoryId: categoryIds[4], serviceName: 'Full Body Massage', basePrice: 800, pricePerUnit: 0, unit: 'service', urgencyMultipliers: { standard: 1.0, express: 1.2, urgent: 1.5 }, isActive: true },
          // Emergency Repair
          { categoryId: categoryIds[5], serviceName: 'Emergency Electrical Fix', basePrice: 500, pricePerUnit: 200, unit: 'hour', urgencyMultipliers: { standard: 1.0, express: 1.5, urgent: 2.0 }, isActive: true },
          // Emergency Delivery
          { categoryId: categoryIds[6], serviceName: 'Emergency Package Delivery', basePrice: 150, pricePerUnit: 25, unit: 'km', urgencyMultipliers: { standard: 1.0, express: 1.5, urgent: 2.0 }, isActive: true },
        ];

        for (const rate of defaultServiceRates) {
          await this.createServiceRate(rate);
        }
      } else {
        console.log('Service rates already exist, skipping creation');
      }

      console.log('Default segments, categories, and service rates created successfully');
      return true;
    } catch (error) {
      console.error('Error initializing default data:', error);
      return false;
    }
  }

  // Analytics
  async getDashboardStats(): Promise<{
    totalNinjas: number;
    pendingNinjas: number;
    approvedNinjas: number;
    totalTasks: number;
    activeTasks: number;
    completedTasks: number;
    totalCustomers: number;
    totalRevenue: number;
  }> {
    try {
      // Issue #358: these eight numbers used to be produced by downloading
      // three entire collections and calling .length / .filter().length on
      // them. On a 5,000-task collection that measured 5,000 document reads
      // and 445ms per dashboard load, growing linearly and without bound.
      //
      // Firestore can count and sum server-side. A count query is billed at a
      // small fraction of the equivalent document reads and returns one number
      // regardless of collection size, so this no longer scales with the data.
      const ninjasRef = collection(db, this.NINJAS_COLLECTION);
      const tasksRef = collection(db, this.TASKS_COLLECTION);
      const usersRef = collection(db, this.USERS_COLLECTION);

      const countOf = async (q: any): Promise<number> => {
        const snap = await getCountFromServer(q);
        return snap.data().count;
      };

      const [
        totalNinjas,
        pendingNinjas,
        approvedNinjas,
        totalTasks,
        activeTasks,
        completedTasks,
        totalCustomers,
        revenueSnap,
      ] = await Promise.all([
        countOf(ninjasRef),
        countOf(query(ninjasRef, where('verificationStatus', '==', 'pending'))),
        countOf(query(ninjasRef, where('verificationStatus', '==', 'approved'))),
        countOf(tasksRef),
        countOf(query(tasksRef, where('status', 'in', ['pending', 'assigned', 'in_progress']))),
        countOf(query(tasksRef, where('status', '==', 'completed'))),
        countOf(usersRef),
        getAggregateFromServer(query(tasksRef, where('status', '==', 'completed')), {
          total: sum('finalPrice'),
        }),
      ]);

      const totalRevenue = revenueSnap.data().total || 0;

      return {
        totalNinjas,
        pendingNinjas,
        approvedNinjas,
        totalTasks,
        activeTasks,
        completedTasks,
        totalCustomers,
        totalRevenue,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        totalNinjas: 0,
        pendingNinjas: 0,
        approvedNinjas: 0,
        totalTasks: 0,
        activeTasks: 0,
        completedTasks: 0,
        totalCustomers: 0,
        totalRevenue: 0,
      };
    }
  }

  // Get growth metrics for dashboard stats
  async getGrowthMetrics(): Promise<{
    revenueGrowth: number;
    taskGrowth: number;
    ninjaGrowth: number;
    customerGrowth: number;
  }> {
    try {
      // Issue #358: this used to download the tasks, ninjas and users
      // collections in full to compare two one-month windows. Every number
      // here is a count or a sum over a date range, which Firestore computes
      // server-side for a fraction of the cost and no document reads.
      const now = new Date();
      const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const tasksRef = collection(db, this.TASKS_COLLECTION);
      const ninjasRef = collection(db, this.NINJAS_COLLECTION);
      const usersRef = collection(db, this.USERS_COLLECTION);

      /** Documents created within [from, to) — `to` omitted means "until now". */
      const inWindow = (
        ref: Query<DocumentData>,
        from: Date,
        to?: Date
      ): Query<DocumentData> => {
        const constraints: any[] = [where('createdAt', '>=', Timestamp.fromDate(from))];
        if (to) constraints.push(where('createdAt', '<', Timestamp.fromDate(to)));
        return query(ref, ...constraints);
      };

      const countOf = async (q: Query<DocumentData>): Promise<number> =>
        (await getCountFromServer(q)).data().count;

      const revenueIn = async (from: Date, to?: Date): Promise<number> => {
        const constraints: any[] = [
          where('status', '==', 'completed'),
          where('createdAt', '>=', Timestamp.fromDate(from)),
        ];
        if (to) constraints.push(where('createdAt', '<', Timestamp.fromDate(to)));
        const snap = await getAggregateFromServer(query(tasksRef, ...constraints), {
          total: sum('finalPrice'),
        });
        return snap.data().total || 0;
      };

      const [
        currentMonthTaskCount,
        currentMonthNinjaCount,
        currentMonthCustomerCount,
        currentMonthRevenue,
        lastMonthTaskCount,
        lastMonthNinjaCount,
        lastMonthCustomerCount,
        lastMonthRevenue,
      ] = await Promise.all([
        countOf(inWindow(tasksRef, currentMonth)),
        countOf(inWindow(ninjasRef, currentMonth)),
        countOf(inWindow(usersRef, currentMonth)),
        revenueIn(currentMonth),
        countOf(inWindow(tasksRef, lastMonth, currentMonth)),
        countOf(inWindow(ninjasRef, lastMonth, currentMonth)),
        countOf(inWindow(usersRef, lastMonth, currentMonth)),
        revenueIn(lastMonth, currentMonth),
      ]);

      // Calculate growth percentages
      const revenueGrowth = lastMonthRevenue > 0
        ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
        : 0;

      const taskGrowth = lastMonthTaskCount > 0
        ? ((currentMonthTaskCount - lastMonthTaskCount) / lastMonthTaskCount) * 100
        : 0;

      const ninjaGrowth = lastMonthNinjaCount > 0
        ? ((currentMonthNinjaCount - lastMonthNinjaCount) / lastMonthNinjaCount) * 100
        : 0;

      const customerGrowth = lastMonthCustomerCount > 0
        ? ((currentMonthCustomerCount - lastMonthCustomerCount) / lastMonthCustomerCount) * 100
        : 0;

      return {
        revenueGrowth: Math.round(revenueGrowth * 100) / 100,
        taskGrowth: Math.round(taskGrowth * 100) / 100,
        ninjaGrowth: Math.round(ninjaGrowth * 100) / 100,
        customerGrowth: Math.round(customerGrowth * 100) / 100,
      };
    } catch (error) {
      console.error('Error calculating growth metrics:', error);
      return {
        revenueGrowth: 0,
        taskGrowth: 0,
        ninjaGrowth: 0,
        customerGrowth: 0,
      };
    }
  }
  async getAnalyticsData(timeRange: string): Promise<{
    revenue: Array<{ month: string; revenue: number; tasks: number }>;
    tasksByStatus: Array<{ status: string; count: number; color: string }>;
    topNinjas: Array<{ name: string; tasks: number; revenue: number }>;
    customerGrowth: Array<{ month: string; customers: number }>;
    performanceMetrics: {
      avgDeliveryTime: number;
      customerSatisfaction: number;
      taskCompletionRate: number;
      revenueGrowth: number;
    };
  }> {
    try {
      // Issue #358: the date range is now part of the query rather than a
      // client-side filter applied after downloading everything. The reads
      // scale with the window the operator selected, not with the lifetime of
      // the business, and `fetchAllPaged` caps even that.
      //
      // The ninja ranking still reads the ninjas collection in full: it ranks
      // every partner, and that collection grows with headcount rather than
      // with transaction volume. It is paged and capped like the rest.
      const startDate = this.getStartDateForRange(timeRange);
      const tasksRef = collection(db, this.TASKS_COLLECTION);
      const usersRef = collection(db, this.USERS_COLLECTION);
      const since = Timestamp.fromDate(startDate);

      const [taskPage, customerPage, ninjas] = await Promise.all([
        this.fetchAllPaged(
          query(tasksRef, where('createdAt', '>=', since), orderBy('createdAt', 'desc')),
          (d) => this.mapTaskDoc(d)
        ),
        this.fetchAllPaged(
          query(usersRef, where('createdAt', '>=', since), orderBy('createdAt', 'desc')),
          (d) => ({ createdAt: d.data().createdAt?.toDate?.() || new Date() })
        ),
        this.getAllNinjas(),
      ]);

      if (taskPage.truncated || customerPage.truncated) {
        console.warn(
          `⚠️ [getAnalyticsData]: the ${timeRange} window exceeds the ${MAX_EXPORT_ROWS}-row read cap; ` +
            'charts are based on the most recent rows in the range.'
        );
      }

      const filteredTasks = taskPage.rows;
      const filteredCustomers = customerPage.rows;

      // Generate revenue data
      const revenue = this.generateRevenueData(filteredTasks, timeRange);

      // Generate tasks by status
      const tasksByStatus = this.generateTasksByStatus(filteredTasks);

      // Issue #243: Generate top ninjas (now async to fetch wallet data)
      const topNinjas = await this.generateTopNinjas(filteredTasks, ninjas);

      // Generate customer growth
      const customerGrowth = this.generateCustomerGrowth(filteredCustomers, timeRange);

      // Calculate performance metrics
      const performanceMetrics = this.calculatePerformanceMetrics(filteredTasks);

      return {
        revenue,
        tasksByStatus,
        topNinjas,
        customerGrowth,
        performanceMetrics,
      };
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  }

  private getStartDateForRange(timeRange: string): Date {
    const now = new Date();
    switch (timeRange) {
      case '1month':
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case '3months':
        return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      case '6months':
        return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      case '1year':
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      default:
        return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    }
  }

  private generateRevenueData(tasks: DeliveryTask[], timeRange: string): Array<{ month: string; revenue: number; tasks: number }> {
    const monthsToShow = this.getMonthsForRange(timeRange);
    const revenueData: Array<{ month: string; revenue: number; tasks: number }> = [];

    for (let i = monthsToShow - 1; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = date.toLocaleString('default', { month: 'short' });
      
      const monthTasks = tasks.filter(task => {
        const taskDate = new Date(task.createdAt);
        return taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
      });

      const monthRevenue = monthTasks
        .filter(task => task.status === 'completed')
        .reduce((sum, task) => {
          const price = task.finalPrice || task.estimatedPrice || 0;
          return sum + Number(price);
        }, 0);

      revenueData.push({
        month: monthKey,
        revenue: monthRevenue,
        tasks: monthTasks.length,
      });
    }

    return revenueData;
  }

  private getMonthsForRange(timeRange: string): number {
    switch (timeRange) {
      case '1month':
        return 1;
      case '3months':
        return 3;
      case '6months':
        return 6;
      case '1year':
        return 12;
      default:
        return 6;
    }
  }

  private generateTasksByStatus(tasks: DeliveryTask[]): Array<{ status: string; count: number; color: string }> {
    const statusCounts = {
      'completed': { count: 0, color: '#38A169' },
      'in_progress': { count: 0, color: '#3182CE' },
      'assigned': { count: 0, color: '#3182CE' },
      'pending': { count: 0, color: '#DD6B20' },
      'cancelled': { count: 0, color: '#E53E3E' },
    };

    tasks.forEach(task => {
      const status = task.status.toLowerCase();
      if (statusCounts[status as keyof typeof statusCounts]) {
        statusCounts[status as keyof typeof statusCounts].count++;
      }
    });

    // Combine similar statuses
    const result = [
      { 
        status: 'Completed', 
        count: statusCounts.completed.count, 
        color: '#38A169' 
      },
      { 
        status: 'In Progress', 
        count: statusCounts.in_progress.count + statusCounts.assigned.count, 
        color: '#3182CE' 
      },
      { 
        status: 'Pending', 
        count: statusCounts.pending.count, 
        color: '#DD6B20' 
      },
      { 
        status: 'Cancelled', 
        count: statusCounts.cancelled.count, 
        color: '#E53E3E' 
      },
    ];

    return result.filter(item => item.count > 0);
  }

  // Issue #243: Updated to use ninja_wallets for accurate earnings calculation
  private async generateTopNinjas(tasks: DeliveryTask[], ninjas: NinjaProfile[]): Promise<Array<{ name: string; tasks: number; revenue: number }>> {
    const ninjaStats: { [key: string]: { name: string; tasks: number; revenue: number } } = {};

    // Create a map for faster ninja lookup
    const ninjaMap = new Map<string, NinjaProfile>();
    ninjas.forEach(ninja => {
      ninjaMap.set(ninja.id, ninja);
      // Also map by userId if available
      if (ninja.userId) {
        ninjaMap.set(ninja.userId, ninja);
      }
    });

    // Issue #243, #257, #265: Fetch all ninja wallets to get actual earnings
    const ninjaWalletsMap = new Map<string, number>(); // ninjaId/userId -> totalEarnings
    try {
      const walletsRef = collection(db, 'ninja_wallets');
      const walletsSnapshot = await getDocs(walletsRef);

      walletsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Issue #265: The document ID is typically the ninjaId
        // Check both totalEarnings and totalCredits fields (different naming conventions)
        const docId = doc.id;
        const totalEarnings = data.totalEarnings || data.totalCredits || 0;

        // Store by document ID (primary key)
        ninjaWalletsMap.set(docId, totalEarnings);

        // Issue #265: Also store by ninjaId field if it exists and is different
        if (data.ninjaId && data.ninjaId !== docId) {
          ninjaWalletsMap.set(data.ninjaId, totalEarnings);
        }
        // Also store by userId field if it exists and is different
        if (data.userId && data.userId !== docId) {
          ninjaWalletsMap.set(data.userId, totalEarnings);
        }

        console.log(`💰 Issue #265: Wallet ${docId} - ninjaId: ${data.ninjaId || 'N/A'}, totalEarnings: ₹${totalEarnings}`);
      });

      console.log(`✅ Issue #265: Loaded ${ninjaWalletsMap.size} ninja wallet entries for earnings calculation`);
    } catch (error) {
      console.error('❌ Issue #265: Error fetching ninja wallets:', error);
    }

    // Issue #264: Count completed tasks per ninja (for fallback calculation)
    const calculatedTaskCounts = new Map<string, number>();
    tasks.forEach(task => {
      // Check both assignedTo and ninjaId fields (tasks might use either)
      const ninjaId = task.assignedTo || (task as any).ninjaId;
      if (ninjaId && task.status === 'completed') {
        calculatedTaskCounts.set(ninjaId, (calculatedTaskCounts.get(ninjaId) || 0) + 1);
      }
    });

    // Issue #264: Build ninja stats using ninjas collection fields with fallback
    // Priority: ninja.totalJobs/totalEarnings > calculated/wallet values
    ninjas.forEach(ninja => {
      const ninjaTotalJobs = (ninja as any).totalJobs || 0;
      const calculatedJobs = calculatedTaskCounts.get(ninja.id) || calculatedTaskCounts.get(ninja.userId || '') || 0;

      // Use ninja.totalJobs if available, otherwise use calculated count
      const taskCount = ninjaTotalJobs > 0 ? ninjaTotalJobs : calculatedJobs;

      if (taskCount > 0) {
        ninjaStats[ninja.id] = {
          name: ninja.fullName || 'Unknown Ninja',
          tasks: taskCount,
          revenue: 0, // Will be set below
        };
        console.log(`📊 Issue #264: ${ninja.fullName} - Using ${ninjaTotalJobs > 0 ? 'ninja.totalJobs' : 'calculated'}: ${taskCount} jobs`);
      }
    });

    // Issue #243, #257, #261, #264: Set revenue from multiple sources with priority:
    // 1. ninja.totalEarnings (from ninjas collection - most reliable)
    // 2. ninja_wallets.totalCredits (wallet collection)
    // 3. Fallback to 0
    Object.keys(ninjaStats).forEach(ninjaId => {
      // Try to find wallet by ninja.id or ninja.userId
      const ninja = ninjaMap.get(ninjaId);
      if (ninja) {
        // Issue #261, #264: First check ninja.totalEarnings from ninjas collection
        // This is the most reliable source as it's directly on the ninja document
        let earnings = 0;

        // Priority 1: ninja.totalEarnings from ninjas collection
        if ((ninja as any).totalEarnings && (ninja as any).totalEarnings > 0) {
          earnings = (ninja as any).totalEarnings;
          console.log(`📊 Issue #264: ${ninja.fullName} - Using ninja.totalEarnings: ₹${earnings}`);
        } else {
          // Priority 2: ninja_wallets.totalCredits
          earnings = ninjaWalletsMap.get(ninja.id) ||
                    ninjaWalletsMap.get(ninja.userId || '') ||
                    ninjaWalletsMap.get(ninjaId) ||
                    0;
          console.log(`📊 Issue #264: ${ninja.fullName} - Using wallet totalCredits: ₹${earnings}`);
        }

        ninjaStats[ninjaId].revenue = earnings;
        console.log(`📊 Issue #264: ${ninja.fullName} - ${ninjaStats[ninjaId].tasks} tasks, ₹${earnings} final earnings`);
      }
    });

    // Sort by completed tasks (highest first) and return top 5
    return Object.values(ninjaStats)
      .filter(stat => stat.tasks > 0) // Only include ninjas with completed tasks
      .sort((a, b) => b.tasks - a.tasks)
      .slice(0, 5);
  }

  /**
   * Signups per month. Takes only `createdAt` (issue #358) — the caller used to
   * pass fully hydrated Customer objects, which meant building them, which meant
   * an addresses read per customer just to count rows on a chart.
   */
  private generateCustomerGrowth(
    customers: Array<{ createdAt: Date }>,
    timeRange: string
  ): Array<{ month: string; customers: number }> {
    const monthsToShow = this.getMonthsForRange(timeRange);
    const growthData: Array<{ month: string; customers: number }> = [];

    for (let i = monthsToShow - 1; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = date.toLocaleString('default', { month: 'short' });
      
      const monthCustomers = customers.filter(customer => {
        const customerDate = new Date(customer.createdAt);
        return customerDate.getMonth() === date.getMonth() && customerDate.getFullYear() === date.getFullYear();
      });

      growthData.push({
        month: monthKey,
        customers: monthCustomers.length,
      });
    }

    return growthData;
  }

  private calculatePerformanceMetrics(tasks: DeliveryTask[]): {
    avgDeliveryTime: number;
    customerSatisfaction: number;
    taskCompletionRate: number;
    revenueGrowth: number;
  } {
    const completedTasks = tasks.filter(task => 
      task.status === 'completed'
    );

    // Calculate average delivery time (mock calculation)
    const avgDeliveryTime = completedTasks.length > 0 
      ? completedTasks.reduce((sum, task) => {
          if (task.completedAt) {
            const timeDiff = new Date(task.completedAt).getTime() - new Date(task.createdAt).getTime();
            return sum + (timeDiff / (1000 * 60 * 60)); // Convert to hours
          }
          return sum + 2.5; // Default estimate
        }, 0) / completedTasks.length
      : 2.5;

    // Calculate customer satisfaction (based on ratings if available)
    const ratedTasks = completedTasks.filter(task => task.rating);
    const customerSatisfaction = ratedTasks.length > 0
      ? ratedTasks.reduce((sum, task) => sum + (task.rating || 0), 0) / ratedTasks.length
      : 4.2; // Default value

    // Calculate task completion rate
    const taskCompletionRate = tasks.length > 0
      ? (completedTasks.length / tasks.length) * 100
      : 0;

    // Calculate revenue growth (comparing with previous period - simplified)
    const now = new Date();
    const currentMonthRevenue = completedTasks
      .filter(task => {
        const taskDate = new Date(task.createdAt);
        return taskDate.getMonth() === now.getMonth() && taskDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, task) => sum + (task.finalPrice || task.estimatedPrice), 0);

    const lastMonthRevenue = completedTasks
      .filter(task => {
        const taskDate = new Date(task.createdAt);
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
        return taskDate.getMonth() === lastMonth.getMonth() && taskDate.getFullYear() === lastMonth.getFullYear();
      })
      .reduce((sum, task) => sum + (task.finalPrice || task.estimatedPrice), 0);

    const revenueGrowth = lastMonthRevenue > 0
      ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
      : 0;

    return {
      avgDeliveryTime: Math.round(avgDeliveryTime * 100) / 100,
      customerSatisfaction: Math.round(customerSatisfaction * 100) / 100,
      taskCompletionRate: Math.round(taskCompletionRate * 100) / 100,
      revenueGrowth: Math.round(revenueGrowth * 100) / 100,
    };
  }

  // Notification Management
  async getAdminNotifications(): Promise<AdminNotification[]> {
    try {
      const notificationsRef = collection(db, 'admin_notifications');
      const q = query(
        notificationsRef,
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
      })) as AdminNotification[];
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
      return [];
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'admin_notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        readAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async createAdminNotification(notification: Omit<AdminNotification, 'id' | 'createdAt' | 'isRead'>): Promise<void> {
    try {
      const notificationsRef = collection(db, 'admin_notifications');
      const docRef = doc(notificationsRef);
      
      await setDoc(docRef, {
        ...notification,
        id: docRef.id,
        createdAt: Timestamp.now(),
        isRead: false,
      });
    } catch (error) {
      console.error('Error creating admin notification:', error);
      throw error;
    }
  }

  async getUnreadNotificationCount(): Promise<number> {
    try {
      const notificationsRef = collection(db, 'admin_notifications');
      const q = query(
        notificationsRef,
        where('isRead', '==', false)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.length;
    } catch (error) {
      console.error('Error getting unread notification count:', error);
      return 0;
    }
  }

  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'admin_notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  // Helper methods to create specific notification types
  async createTaskNotification(taskId: string, type: 'created' | 'assigned' | 'completed' | 'failed', details: any): Promise<void> {
    let title = '';
    let message = '';
    let priority: AdminNotification['priority'] = 'medium';

    switch (type) {
      case 'created':
        title = 'New Task Created';
        message = `Task ${taskId} has been created and is pending assignment.`;
        priority = 'medium';
        break;
      case 'assigned':
        title = 'Task Assigned';
        message = `Task ${taskId} has been assigned to ${details.ninjaName}.`;
        priority = 'low';
        break;
      case 'completed':
        title = 'Task Completed';
        message = `Task ${taskId} has been successfully completed by ${details.ninjaName}.`;
        priority = 'low';
        break;
      case 'failed':
        title = 'Task Failed';
        message = `Task ${taskId} has failed: ${details.reason}`;
        priority = 'high';
        break;
    }

    await this.createAdminNotification({
      title,
      message,
      type: 'task',
      priority,
      actionUrl: `/tasks?taskId=${taskId}`,
      actionText: 'View Task',
      relatedId: taskId,
      relatedType: 'task',
      metadata: details,
    });
  }

  async createNinjaNotification(ninjaId: string, type: 'registered' | 'approved' | 'rejected' | 'suspended', details: any): Promise<void> {
    let title = '';
    let message = '';
    let priority: AdminNotification['priority'] = 'medium';

    switch (type) {
      case 'registered':
        title = 'New Ninja Registration';
        message = `${details.fullName} has registered as a ninja and is pending approval.`;
        priority = 'high';
        break;
      case 'approved':
        title = 'Ninja Approved';
        message = `${details.fullName} has been approved and is now active.`;
        priority = 'low';
        break;
      case 'rejected':
        title = 'Ninja Rejected';
        message = `${details.fullName} has been rejected.`;
        priority = 'low';
        break;
      case 'suspended':
        title = 'Ninja Suspended';
        message = `${details.fullName} has been suspended due to ${details.reason}.`;
        priority = 'high';
        break;
    }

    await this.createAdminNotification({
      title,
      message,
      type: 'ninja',
      priority,
      actionUrl: `/ninjas?ninjaId=${ninjaId}`,
      actionText: 'View Ninja',
      relatedId: ninjaId,
      relatedType: 'ninja',
      metadata: details,
    });
  }

  async createSystemNotification(type: 'error' | 'warning' | 'maintenance', title: string, message: string, priority: AdminNotification['priority'] = 'medium'): Promise<void> {
    await this.createAdminNotification({
      title,
      message,
      type: 'system',
      priority,
      metadata: { systemEvent: true },
    });
  }

  // Admin Profile Management Methods
  async updateAdminProfile(profileData: {
    displayName?: string;
    email?: string;
    phone?: string;
    profilePicture?: string;
  }): Promise<boolean> {
    try {
      // Here you would update the admin's Firebase Auth profile
      // and any additional profile data stored in Firestore
      console.log('Updating admin profile:', profileData);
      
      // Simulated update - replace with actual Firebase Auth update
      // const user = auth.currentUser;
      // if (user) {
      //   await updateProfile(user, {
      //     displayName: profileData.displayName,
      //     photoURL: profileData.profilePicture
      //   });
      // }
      
      return true;
    } catch (error) {
      console.error('Error updating admin profile:', error);
      return false;
    }
  }

  async updateAdminPreferences(preferences: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    taskNotifications?: boolean;
    securityAlerts?: boolean;
    darkMode?: boolean;
    language?: string;
    timezone?: string;
  }): Promise<boolean> {
    try {
      // Store admin preferences in Firestore
      const adminId = 'admin-user'; // Replace with actual admin ID from auth
      const preferencesRef = doc(db, 'adminPreferences', adminId);
      
      await setDoc(preferencesRef, {
        ...preferences,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error updating admin preferences:', error);
      return false;
    }
  }

  async updateSecuritySettings(settings: {
    twoFactorEnabled?: boolean;
    sessionTimeout?: number;
    requirePasswordChange?: boolean;
  }): Promise<boolean> {
    try {
      // Store security settings in Firestore
      const adminId = 'admin-user'; // Replace with actual admin ID from auth
      const securityRef = doc(db, 'adminSecurity', adminId);
      
      await setDoc(securityRef, {
        ...settings,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error updating security settings:', error);
      return false;
    }
  }

  async getAdminPreferences(): Promise<any> {
    try {
      const adminId = 'admin-user'; // Replace with actual admin ID from auth
      const preferencesRef = doc(db, 'adminPreferences', adminId);
      const docSnapshot = await getDoc(preferencesRef);
      
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      }
      
      // Return default preferences if none exist
      return {
        emailNotifications: true,
        pushNotifications: true,
        taskNotifications: true,
        securityAlerts: true,
        darkMode: false,
        language: 'en',
        timezone: 'Asia/Kolkata',
      };
    } catch (error) {
      console.error('Error fetching admin preferences:', error);
      return null;
    }
  }

  async getSecuritySettings(): Promise<any> {
    try {
      const adminId = 'admin-user'; // Replace with actual admin ID from auth
      const securityRef = doc(db, 'adminSecurity', adminId);
      const docSnapshot = await getDoc(securityRef);
      
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      }
      
      // Return default security settings if none exist
      return {
        twoFactorEnabled: false,
        sessionTimeout: 30,
        requirePasswordChange: false,
      };
    } catch (error) {
      console.error('Error fetching security settings:', error);
      return null;
    }
  }

  async logoutAllDevices(): Promise<boolean> {
    try {
      // Here you would invalidate all active sessions
      // This could involve updating a session token in Firestore
      // and forcing all other instances to re-authenticate
      console.log('Logging out from all devices');
      
      const adminId = 'admin-user'; // Replace with actual admin ID from auth
      const sessionRef = doc(db, 'adminSessions', adminId);
      
      await setDoc(sessionRef, {
        invalidateAllSessions: true,
        invalidatedAt: Timestamp.now(),
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error logging out from all devices:', error);
      return false;
    }
  }

  async getAdminActivityLog(): Promise<Array<{
    id: string;
    action: string;
    timestamp: Date;
    ip: string;
    userAgent?: string;
  }>> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getAdminActivityLog]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getAdminActivityLog]: Admin authenticated:', currentUser.email);
      
      const adminId = currentUser.uid; // Use actual admin ID from auth
      const activityQuery = query(
        collection(db, 'adminActivityLog'),
        where('adminId', '==', adminId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(activityQuery);
      const activities = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate(),
      }));
      
      return activities as any[];
    } catch (error) {
      console.error('Error fetching admin activity log:', error);
      return [];
    }
  }

  async logAdminActivity(
    action: string,
    ip: string = 'unknown',
    userAgent?: string,
    context?: { cityId?: string; actingAsCityId?: string; isImpersonating?: boolean }
  ): Promise<void> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [logAdminActivity]: Admin not authenticated');
        return; // Don't throw error for logging, just skip
      }

      console.log('✅ [logAdminActivity]: Admin authenticated:', currentUser.email);

      const adminId = currentUser.uid; // Use actual admin ID from auth
      const activityRef = collection(db, 'adminActivityLog');

      const activityData: any = {
        adminId,
        action,
        ip,
        timestamp: Timestamp.now(),
      };

      // Only add userAgent if it's provided and not undefined
      if (userAgent) {
        activityData.userAgent = userAgent;
      }
      if (context?.cityId) activityData.cityId = context.cityId;
      if (context?.actingAsCityId) activityData.actingAsCityId = context.actingAsCityId;
      if (context?.isImpersonating) activityData.isImpersonating = true;

      await setDoc(doc(activityRef), activityData);
    } catch (error) {
      console.error('Error logging admin activity:', error);
    }
  }

  /**
   * The full admin activity log across every admin — not just the caller's
   * own entries, unlike getAdminActivityLog(). This is the "who is doing
   * what" view requirement #5 calls for, most importantly for actions taken
   * while impersonating a city.
   */
  async getAllAdminActivityLog(maxRows: number = 200): Promise<AdminActivityLogEntry[]> {
    try {
      const activityQuery = query(
        collection(db, 'adminActivityLog'),
        orderBy('timestamp', 'desc'),
        limit(maxRows)
      );
      const snapshot = await getDocs(activityQuery);
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          adminId: data.adminId,
          action: data.action,
          cityId: data.cityId,
          actingAsCityId: data.actingAsCityId,
          isImpersonating: data.isImpersonating || false,
          ip: data.ip,
          userAgent: data.userAgent,
          timestamp: data.timestamp?.toDate?.() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error fetching full admin activity log:', error);
      return [];
    }
  }

  // ── Multi-city: cities, admin users, ninja provisioning, city catalog ──────
  // See functions/src/lib/adminAuth.ts and HyperlocalApp/firestore.rules for
  // the role/city enforcement this UI-layer code relies on — every method
  // here trusts the server (rules or the Cloud Functions below) to reject
  // what it shouldn't, the same way the rest of this service does.

  /** The signed-in user's own admin_users document — role, cityId, etc. */
  async getCurrentAdminRecord(): Promise<AdminUserRecord | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    try {
      const snap = await getDoc(doc(db, this.ADMIN_USERS_COLLECTION, currentUser.uid));
      if (!snap.exists()) return null;
      const data = snap.data();
      return {
        uid: snap.id,
        email: data.email,
        name: data.name,
        // Docs created before roles existed have no `role` field — treat
        // that as super_admin, matching the Firestore rules and Cloud
        // Functions so all three layers agree on the same accounts.
        role: data.role === 'city_admin' ? 'city_admin' : 'super_admin',
        cityId: data.cityId ?? null,
        isActive: data.isActive !== false,
        createdAt: data.createdAt?.toDate?.(),
        createdBy: data.createdBy,
      };
    } catch (error) {
      console.error('Error fetching current admin record:', error);
      return null;
    }
  }

  async getAllCities(): Promise<City[]> {
    try {
      const snapshot = await getDocs(
        query(collection(db, this.CITIES_COLLECTION), orderBy('name', 'asc'))
      );
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name,
          state: data.state,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  }

  async createCity(name: string, state: string): Promise<string> {
    const ref = await addDoc(collection(db, this.CITIES_COLLECTION), {
      name: name.trim(),
      state: state.trim(),
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    await this.logAdminActivity(`Created city "${name.trim()}"`);
    return ref.id;
  }

  async updateCity(cityId: string, updates: Partial<Pick<City, 'name' | 'state' | 'isActive'>>): Promise<void> {
    await updateDoc(doc(db, this.CITIES_COLLECTION, cityId), {
      ...updates,
      updatedAt: Timestamp.now(),
    });
    await this.logAdminActivity(`Updated city ${cityId}`, undefined, undefined, { cityId });
  }

  /** Cities are never hard-deleted — ninjas, admins and rates reference them
   *  by id, and a City that vanishes out from under an admin_users doc would
   *  leave that admin in an unrecoverable state (resolveTargetCityId in the
   *  Cloud Functions would reject every request they make). Deactivating is
   *  the reversible equivalent: the city drops out of pickers everywhere it
   *  matters without breaking anything that still points at it. */
  async deactivateCity(cityId: string): Promise<void> {
    await this.updateCity(cityId, { isActive: false });
  }

  async getAllAdminUsers(): Promise<AdminUserRecord[]> {
    try {
      const snapshot = await getDocs(collection(db, this.ADMIN_USERS_COLLECTION));
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          uid: d.id,
          email: data.email,
          name: data.name,
          role: data.role === 'city_admin' ? 'city_admin' : 'super_admin',
          cityId: data.cityId ?? null,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate?.(),
          createdBy: data.createdBy,
        };
      });
    } catch (error) {
      console.error('Error fetching admin users:', error);
      return [];
    }
  }

  /**
   * Creates a city-scoped admin account. Goes through the adminCreateAdminUser
   * Cloud Function rather than a direct Firestore write, because creating the
   * Firebase Auth account itself requires the Admin SDK — the client SDK has
   * no way to create a user without signing the caller out of their own
   * session. Returns a one-time temporary password; nothing here emails it,
   * so the caller (the UI) is the only place that ever sees it.
   */
  async createAdminUser(email: string, name: string, cityId: string): Promise<{ uid: string; tempPassword: string }> {
    const callable = httpsCallable<{ email: string; name: string; cityId: string }, { uid: string; email: string; tempPassword: string }>(
      functions,
      'adminCreateAdminUser'
    );
    const result = await callable({ email: email.trim(), name: name.trim(), cityId });
    await this.logAdminActivity(`Created city admin "${name.trim()}" (${email.trim()})`, undefined, undefined, { cityId });
    return { uid: result.data.uid, tempPassword: result.data.tempPassword };
  }

  async setAdminUserActive(uid: string, isActive: boolean): Promise<void> {
    await updateDoc(doc(db, this.ADMIN_USERS_COLLECTION, uid), {
      isActive,
      updatedAt: Timestamp.now(),
    });
    await this.logAdminActivity(`${isActive ? 'Reactivated' : 'Deactivated'} admin ${uid}`);
  }

  /**
   * Creates a ninja directly from the admin panel (requirement #1) — pre-
   * approved, since an admin vouched for them, so they skip the normal
   * self-registration review queue. Goes through a Cloud Function for the
   * same reason createAdminUser does: creating the Firebase Auth account
   * needs the Admin SDK.
   */
  async createNinja(input: {
    fullName: string;
    phoneNumber: string;
    email?: string;
    cityId?: string; // required for super_admin, ignored/validated for city_admin
  }): Promise<{ uid: string; phoneNumber: string; cityId: string }> {
    const callable = httpsCallable<typeof input, { uid: string; phoneNumber: string; cityId: string }>(
      functions,
      'adminCreateNinja'
    );
    const result = await callable(input);
    await this.logAdminActivity(`Added ninja "${input.fullName}" (${input.phoneNumber})`, undefined, undefined, {
      cityId: result.data.cityId,
    });
    return result.data;
  }

  /** All city_service_rates overrides for one city, keyed by rateId for O(1)
   *  lookup against the master service_rates list the caller already has. */
  async getCityServiceRates(cityId: string): Promise<Record<string, CityServiceRate>> {
    try {
      const snapshot = await getDocs(
        query(collection(db, this.CITY_SERVICE_RATES_COLLECTION), where('cityId', '==', cityId))
      );
      const byRateId: Record<string, CityServiceRate> = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        byRateId[data.rateId] = {
          id: d.id,
          cityId: data.cityId,
          rateId: data.rateId,
          isEnabled: data.isEnabled !== false,
          overridePrice: data.overridePrice,
          overrideUrgencyMultipliers: data.overrideUrgencyMultipliers,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        };
      });
      return byRateId;
    } catch (error) {
      console.error('Error fetching city service rates:', error);
      return {};
    }
  }

  /**
   * Turns a master rate on/off for a city, and/or sets its price override —
   * the only two things a city_admin may do to the catalog (requirement #4).
   * The document id is `{cityId}_{rateId}`, matching the Firestore rule that
   * enforces a city_admin can only ever write under their own cityId.
   */
  async setCityServiceRate(
    cityId: string,
    rateId: string,
    updates: { isEnabled?: boolean; overridePrice?: number; overrideUrgencyMultipliers?: CityServiceRate['overrideUrgencyMultipliers'] }
  ): Promise<void> {
    const docId = `${cityId}_${rateId}`;
    const ref = doc(db, this.CITY_SERVICE_RATES_COLLECTION, docId);
    const existing = await getDoc(ref);
    await setDoc(
      ref,
      {
        cityId,
        rateId,
        ...(existing.exists() ? {} : { createdAt: Timestamp.now() }),
        ...updates,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
    await this.logAdminActivity(`Set city rate override for ${rateId}`, undefined, undefined, { cityId });
  }

  // Banner Ads Management
  async getAllBannerAds(): Promise<BannerAd[]> {
    try {
      // Check authentication first
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('❌ [getAllBannerAds]: Admin not authenticated');
        throw new Error('Authentication required');
      }

      console.log('✅ [getAllBannerAds]: Admin authenticated:', currentUser.email);

      // Issue #274: Only fetch active banner ads (soft delete support)
      const bannersRef = collection(db, this.BANNER_ADS_COLLECTION);
      const q = query(bannersRef, where('isActive', '==', true), orderBy('displayOrder', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as BannerAd;
      });
    } catch (error) {
      console.error('Error getting banner ads:', error);
      return [];
    }
  }

  async createBannerAd(bannerData: Omit<BannerAd, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const bannersRef = collection(db, this.BANNER_ADS_COLLECTION);
      const newBannerRef = doc(bannersRef);
      
      const bannerWithTimestamps = {
        ...bannerData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(newBannerRef, bannerWithTimestamps);
      
      await this.logAdminActivity(`Created banner ad: ${bannerData.bannerText}`);
      
      return newBannerRef.id;
    } catch (error) {
      console.error('Error creating banner ad:', error);
      throw error;
    }
  }

  async updateBannerAd(bannerId: string, updates: Partial<Omit<BannerAd, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const bannerRef = doc(db, this.BANNER_ADS_COLLECTION, bannerId);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
      };
      
      await updateDoc(bannerRef, updateData);
      
      await this.logAdminActivity(`Updated banner ad: ${bannerId}`);
    } catch (error) {
      console.error('Error updating banner ad:', error);
      throw error;
    }
  }

  async deleteBannerAd(bannerId: string): Promise<void> {
    try {
      // Issue #274: Soft delete - set isActive to false instead of permanent deletion
      const bannerRef = doc(db, this.BANNER_ADS_COLLECTION, bannerId);
      await updateDoc(bannerRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ Issue #274: Banner ad ${bannerId} soft deleted (isActive: false)`);
      await this.logAdminActivity(`Soft deleted banner ad: ${bannerId}`);
    } catch (error) {
      console.error('❌ Issue #274: Error soft deleting banner ad:', error);
      throw error;
    }
  }

  async uploadBannerImage(file: File): Promise<string> {
    try {
      // Generate a unique filename with timestamp
      const timestamp = Date.now();
      const filename = `banner_images/${timestamp}_${file.name}`;
      
      // Create a reference to Firebase Storage
      const storageRef = ref(storage, filename);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Log the activity
      await this.logAdminActivity(`Uploaded banner image: ${file.name}`);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading banner image:', error);
      throw new Error('Failed to upload image. Please try again.');
    }
  }

  async getBannerAd(bannerId: string): Promise<BannerAd | null> {
    try {
      const bannerRef = doc(db, this.BANNER_ADS_COLLECTION, bannerId);
      const bannerDoc = await getDoc(bannerRef);
      
      if (!bannerDoc.exists()) {
        return null;
      }
      
      const data = bannerDoc.data();
      return {
        id: bannerDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as BannerAd;
    } catch (error) {
      console.error('Error getting banner ad:', error);
      return null;
    }
  }

  async getActiveBannerAds(): Promise<BannerAd[]> {
    try {
      const bannersRef = collection(db, this.BANNER_ADS_COLLECTION);
      const q = query(
        bannersRef, 
        where('isActive', '==', true),
        orderBy('displayOrder', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as BannerAd;
      });
    } catch (error) {
      console.error('Error getting active banner ads:', error);
      return [];
    }
  }

  // ==================== SERVICEABLE AREAS MANAGEMENT ====================

  async getAllServiceableAreas(): Promise<any[]> {
    try {
      // Issue #271: Only fetch active serviceable areas (soft delete support)
      const areasRef = collection(db, 'serviceable_areas');
      const q = query(areasRef, where('isActive', '==', true), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error getting serviceable areas:', error);
      throw error;
    }
  }

  async getServiceableArea(areaId: string): Promise<any | null> {
    try {
      const areaRef = doc(db, 'serviceable_areas', areaId);
      const areaDoc = await getDoc(areaRef);
      
      if (!areaDoc.exists()) {
        return null;
      }
      
      const data = areaDoc.data();
      return {
        id: areaDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('Error getting serviceable area:', error);
      return null;
    }
  }

  async createServiceableArea(areaData: {
    areaName: string;
    maxDeliveryRadius: number;
    isActive: boolean;
    order: number;
    boundaries: {
      type: 'circle';
      center: {
        latitude: number;
        longitude: number;
      };
      radius: number;
    };
  }): Promise<string> {
    try {
      const areasRef = collection(db, 'serviceable_areas');
      const docRef = await addDoc(areasRef, {
        ...areaData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      
      await this.logAdminActivity(`Created serviceable area: ${areaData.areaName}`);
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating serviceable area:', error);
      throw error;
    }
  }

  async updateServiceableArea(areaId: string, updates: any): Promise<void> {
    try {
      const areaRef = doc(db, 'serviceable_areas', areaId);

      // Fetch area name for logging before update
      const areaDoc = await getDoc(areaRef);
      const areaName = areaDoc.exists() ? areaDoc.data().areaName || areaId : areaId;

      await updateDoc(areaRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });

      await this.logAdminActivity(`Updated serviceable area: ${areaName}`);
    } catch (error) {
      console.error('Error updating serviceable area:', error);
      throw error;
    }
  }

  async deleteServiceableArea(areaId: string): Promise<void> {
    try {
      // Issue #271: Soft delete - set isActive to false instead of permanent deletion
      const areaRef = doc(db, 'serviceable_areas', areaId);

      // Fetch area name for logging before soft deletion
      const areaDoc = await getDoc(areaRef);
      const areaName = areaDoc.exists() ? areaDoc.data().areaName || areaId : areaId;

      await updateDoc(areaRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ Issue #271: Serviceable area ${areaId} soft deleted (isActive: false)`);
      await this.logAdminActivity(`Soft deleted serviceable area: ${areaName}`);
    } catch (error) {
      console.error('❌ Issue #271: Error soft deleting serviceable area:', error);
      throw error;
    }
  }

  async getAreaStatistics(areaId: string): Promise<{
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
    totalRevenue: number;
    averageTaskValue: number;
  }> {
    try {
      console.log(`📊 [getAreaStatistics]: Calculating statistics for area: ${areaId}`);

      const tasksRef = collection(db, 'tasks');

      // Query tasks that match this area - checking multiple fields
      // For service tasks: selectedArea.areaId
      // For delivery tasks: pickupLocation.areaId or dropLocation.areaId
      const queries = [
        query(tasksRef, where('selectedArea.areaId', '==', areaId)),
        query(tasksRef, where('pickupLocation.areaId', '==', areaId)),
        query(tasksRef, where('dropLocation.areaId', '==', areaId))
      ];

      // Execute all queries and combine results
      const querySnapshots = await Promise.all(queries.map(q => getDocs(q)));

      // Use a Set to deduplicate tasks (in case a task matches multiple queries)
      const uniqueTaskIds = new Set<string>();
      const tasksData: any[] = [];

      querySnapshots.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          if (!uniqueTaskIds.has(doc.id)) {
            uniqueTaskIds.add(doc.id);
            tasksData.push({ id: doc.id, ...doc.data() });
          }
        });
      });

      console.log(`✅ [getAreaStatistics]: Found ${tasksData.length} unique tasks for area ${areaId}`);

      let completedTasks = 0;
      let pendingTasks = 0;
      let totalRevenue = 0;

      tasksData.forEach(task => {
        if (task.status === 'completed') {
          completedTasks++;
          // Handle revenue for both delivery and service tasks
          // Service tasks may use 'subtotal' while delivery tasks use 'finalPrice' or 'estimatedPrice'
          const taskRevenue = task.finalPrice || task.subtotal || task.estimatedPrice || 0;
          totalRevenue += taskRevenue;
        } else if (task.status === 'pending' || task.status === 'assigned' || task.status === 'in_progress') {
          pendingTasks++;
        }
      });

      const totalTasks = tasksData.length;
      const averageTaskValue = totalTasks > 0 && completedTasks > 0 ? totalRevenue / completedTasks : 0;

      console.log(`📊 [getAreaStatistics]: Stats for ${areaId} - Total: ${totalTasks}, Completed: ${completedTasks}, Pending: ${pendingTasks}, Revenue: ₹${totalRevenue}`);

      return {
        totalTasks,
        completedTasks,
        pendingTasks,
        totalRevenue,
        averageTaskValue,
      };
    } catch (error) {
      // Handle permission errors or missing data gracefully
      console.warn('⚠️ Could not fetch area statistics for', areaId, '- returning defaults:', error);
      return {
        totalTasks: 0,
        completedTasks: 0,
        pendingTasks: 0,
        totalRevenue: 0,
        averageTaskValue: 0,
      };
    }
  }

}

export default new AdminService();